import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Plus, Search, X, Check, Trash2, RefreshCw,
  Eye, AlertCircle, Send, FileText, Video,
  ChevronRight, ChevronLeft, AlertTriangle,
  ExternalLink, Download, Play, Folder,
  Building2, ChevronDown, ArrowUpDown, Link,
  BarChart2, TrendingUp, Clock, Users, Calendar
} from 'lucide-react';

/* ─── Design Tokens ──────────────────────────────────────── */
const T = {
  bg:'#f8f7ff', surface:'#ffffff', border:'#e8e4f3', borderL:'#f5f3ff',
  purple:'#7c3aed', purpleL:'#8b5cf6', purpleBg:'#ede9fe', purpleText:'#6d28d9',
  text:'#1a1035', textMd:'#4a3f6b', textSm:'#8b7fb0', textXs:'#b8b3cc',
  green:'#059669', greenBg:'#d1fae5',
  red:'#dc2626', redBg:'#fee2e2',
  yellow:'#d97706', yellowBg:'#fef3c7',
  orange:'#ea580c', orangeBg:'#ffedd5',
  blue:'#2563eb', blueBg:'#dbeafe',
  pink:'#db2777', pinkBg:'#fce7f3',
  teal:'#0d9488', tealBg:'#ccfbf1',
  indigo:'#4f46e5', indigoBg:'#e0e7ff',
  amber:'#b45309', amberBg:'#fef3c7',
  font:"'Poppins', sans-serif",
  shadow:'0 1px 4px rgba(124,58,237,0.06)',
  shadowMd:'0 4px 20px rgba(124,58,237,0.1)',
};

/* ─── Status config — all 10 statuses ──────────────────────── */
const STATUS = {
  ready_to_edit:   { label:'Ready to Edit',    bg:'#d1fae5', color:'#059669', border:'#6ee7b7' },
  editing:         { label:'Editing',          bg:'#ccfbf1', color:'#0d9488', border:'#5eead4' },
  review:          { label:'Review',           bg:'#ede9fe', color:'#7c3aed', border:'#c4b5fd' },
  rework:          { label:'Rework',           bg:'#fee2e2', color:'#dc2626', border:'#fca5a5' },
  client_approval: { label:'Client Approval',  bg:'#dbeafe', color:'#2563eb', border:'#93c5fd' },
  client_rework:   { label:'Client Rework',    bg:'#ffedd5', color:'#ea580c', border:'#fdba74' },
  client_approved: { label:'Approved ✓',       bg:'#d1fae5', color:'#059669', border:'#6ee7b7' },
  ready_to_upload: { label:'Ready to Upload',  bg:'#e0e7ff', color:'#4f46e5', border:'#a5b4fc' },
  correction:      { label:'Correction',       bg:'#fef3c7', color:'#b45309', border:'#fcd34d' },
  uploaded:        { label:'Uploaded ✓',       bg:'#f0fdf4', color:'#16a34a', border:'#86efac' },
};

// Active statuses shown in main view (not completed)
const ACTIVE_STATUSES = ['ready_to_edit','editing','review','rework','client_approval','client_rework','client_approved','ready_to_upload','correction'];
const COMPLETED_STATUSES = ['uploaded'];

// Priority color bars
const PRIORITY_COLORS = [
  { color:'#dc2626', label:'Urgent',  bg:'#fee2e2' },
  { color:'#ea580c', label:'High',    bg:'#ffedd5' },
  { color:'#1868f0', label:'Normal',  bg:'#dbeafe' },
  { color:'#d97706', label:'Medium',  bg:'#fef3c7' },
  { color:'#059669', label:'Low',     bg:'#d1fae5' },
];

/* ─── Helpers ─────────────────────────────────────────────── */
const fmtDate = d => { if(!d) return '—'; const dt=new Date(d+'T00:00:00'); return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`; };
const ageDays = created => Math.floor((Date.now()-new Date(created))/86400000);
const isOverdue = (due, status) => due && due < new Date().toISOString().split('T')[0] && !COMPLETED_STATUSES.includes(status);
const isToday = d => d === new Date().toISOString().split('T')[0];
const isThisWeek = d => { const dt=new Date(d+'T00:00:00'), now=new Date(); const diff=(dt-now)/86400000; return diff>=0&&diff<=7; };

/* ─── Shared UI ───────────────────────────────────────────── */
const Spinner = ({size=24}) => (
  <div style={{width:size,height:size,border:'3px solid #ede9fe',borderTop:`3px solid ${T.purple}`,borderRadius:'50%',animation:'spin 0.8s linear infinite',flexShrink:0}}/>
);

const Btn = ({children,onClick,variant='primary',small,disabled,full}) => {
  const v = {
    primary:  {bg:T.purple,  c:'#fff', b:'none',              sh:'0 2px 8px rgba(124,58,237,0.25)'},
    secondary:{bg:'#fff',    c:T.textMd, b:`1.5px solid ${T.border}`, sh:'none'},
    green:    {bg:T.green,   c:'#fff', b:'none',              sh:'0 2px 8px rgba(5,150,105,0.2)'},
    red:      {bg:T.red,     c:'#fff', b:'none',              sh:'none'},
    ghost:    {bg:'transparent',c:T.textSm,b:`1px solid ${T.border}`,sh:'none'},
  }[variant]||{};
  return (
    <button onClick={onClick} disabled={disabled}
      style={{display:'flex',alignItems:'center',gap:5,background:v.bg,color:v.c,border:v.b,
        borderRadius:9,padding:small?'6px 12px':'9px 16px',fontSize:small?12:13,fontWeight:600,
        cursor:disabled?'not-allowed':'pointer',fontFamily:T.font,opacity:disabled?0.6:1,
        whiteSpace:'nowrap',boxShadow:v.sh,width:full?'100%':'auto',
        justifyContent:full?'center':'flex-start',transition:'all 0.15s'}}>
      {children}
    </button>
  );
};

const Modal = ({title,subtitle,onClose,children,actions,wide}) => (
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',
    justifyContent:'center',zIndex:3000,backdropFilter:'blur(4px)',fontFamily:T.font,padding:16,overflowY:'auto'}}>
    <div style={{background:'#fff',border:`1px solid ${T.border}`,borderRadius:18,padding:'24px 26px',
      width:'100%',maxWidth:wide?600:480,boxShadow:T.shadowMd,maxHeight:'92vh',overflow:'auto',margin:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18}}>
        <div>
          <div style={{fontWeight:700,fontSize:17,color:T.text}}>{title}</div>
          {subtitle&&<div style={{fontWeight:400,fontSize:13,color:T.textSm,marginTop:3}}>{subtitle}</div>}
        </div>
        <button onClick={onClose} style={{background:'#f5f3ff',border:'none',cursor:'pointer',color:T.textSm,padding:6,borderRadius:8,display:'flex'}}><X size={16}/></button>
      </div>
      {children}
      {actions&&<div style={{display:'flex',justifyContent:'flex-end',gap:9,marginTop:20}}>{actions}</div>}
    </div>
  </div>
);

// Delete confirmation popup
const ConfirmDeleteModal = ({fileName, onConfirm, onCancel}) => (
  <Modal title="Remove File" onClose={onCancel}>
    <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'12px 14px',marginBottom:14}}>
      <div style={{fontWeight:600,fontSize:13,color:'#9a3412',marginBottom:4}}>Are you sure?</div>
      <div style={{fontSize:12,color:'#92400e'}}>Remove <strong>{fileName}</strong> from this task?</div>
    </div>
    <div style={{display:'flex',justifyContent:'flex-end',gap:9}}>
      <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
      <Btn variant="red" onClick={onConfirm}>Remove</Btn>
    </div>
  </Modal>
);

const inputSt = {width:'100%',border:`1.5px solid ${T.border}`,borderRadius:9,padding:'9px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#faf9ff',outline:'none',boxSizing:'border-box'};

/* ─── Priority Color Bar ─────────────────────────────────── */
const ColorBar = ({color, size=16, onClick, title}) => (
  <div onClick={onClick} title={title}
    style={{width:size,height:size,borderRadius:4,background:color||'#1868f0',cursor:onClick?'pointer':'default',flexShrink:0,border:'1.5px solid rgba(0,0,0,0.1)',boxShadow:'0 1px 3px rgba(0,0,0,0.12)',transition:'transform 0.1s'}}/>
);

// Color picker popup
function ColorPicker({value, onChange, onClose}) {
  const ref = useRef();
  useEffect(()=>{ const h=e=>{if(ref.current&&!ref.current.contains(e.target))onClose();}; document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h); },[]);
  return (
    <div ref={ref} style={{position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:200,background:'#fff',border:`1px solid ${T.border}`,borderRadius:10,padding:'10px',boxShadow:T.shadowMd,display:'flex',gap:7}}>
      {PRIORITY_COLORS.map(p=>(
        <div key={p.color} onClick={()=>{onChange(p.color);onClose();}} title={p.label}
          style={{width:22,height:22,borderRadius:6,background:p.color,cursor:'pointer',border:value===p.color?`2.5px solid ${T.text}`:'2px solid transparent',transition:'all 0.1s'}}/>
      ))}
    </div>
  );
}

/* ─── StatusChip with dropdown ───────────────────────────── */
function StatusChip({status, taskId, role, onChanged}) {
  const [open, setOpen] = useState(false);
  const [showRework, setShowRework] = useState(false);
  const [showCorrection, setShowCorrection] = useState(false);
  const [reworkNotes, setReworkNotes] = useState('');
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const ref = useRef();

  useEffect(()=>{ const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);}; document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h); },[]);

  const cfg = STATUS[status] || STATUS.ready_to_edit;

  const roleOptions = {
    editor: ['editing','review'],
    smm:    ['ready_to_edit','review','rework','client_approval','ready_to_upload','correction','uploaded'],
    admin:  Object.keys(STATUS),
  };
  const opts = roleOptions[role] || roleOptions.admin;

  const changeStatus = async (newStatus) => {
    if (newStatus === 'rework') { setShowRework(true); setOpen(false); return; }
    if (newStatus === 'correction') { setShowCorrection(true); setOpen(false); return; }
    if (newStatus === 'client_approval') {
  setSaving(true);
  try {
    const r = await api.post(`/tasks/${taskId}/approve-link`);
    const link = r.data.approval_link;  // ← fix: extract from r.data

    // Copy to clipboard — no WhatsApp redirect
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const el = document.createElement('textarea');
      el.value = link;                  // ← fix: use `link` not `approval_link`
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    toast.success('Review link copied! Share it with the client manually.');
    onChanged?.();                      // ← fix: use onChanged not load()
  } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  finally { setSaving(false); setOpen(false); }
  return;
}
    setSaving(true);
    try {
      await api.put(`/tasks/${taskId}/status`,{status:newStatus});
      toast.success(`Status → ${STATUS[newStatus]?.label}`);
      onChanged?.();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSaving(false); setOpen(false); }
  };

  const submitRework = async () => {
    if (!reworkNotes.trim()) return toast.error('Describe what needs to be changed');
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/rework`,{notes:reworkNotes,rework_type:'internal'});
      toast.success('Rework request sent to editor');
      setShowRework(false); setReworkNotes(''); onChanged?.();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  const submitCorrection = async () => {
    if (!correctionNotes.trim()) return toast.error('Describe the correction needed');
    setSaving(true);
    try {
      await api.post(`/tasks/${taskId}/rework`,{notes:correctionNotes,rework_type:'correction'});
      toast.success('Correction request sent to editor');
      setShowCorrection(false); setCorrectionNotes(''); onChanged?.();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div ref={ref} style={{position:'relative',display:'inline-block'}}>
        <button onClick={()=>!saving&&setOpen(o=>!o)} disabled={saving}
          style={{display:'flex',alignItems:'center',gap:5,background:cfg.bg,border:`1.5px solid ${cfg.border}`,
            color:cfg.color,borderRadius:20,padding:'4px 10px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:T.font,whiteSpace:'nowrap'}}>
          {saving?<Spinner size={10}/>:cfg.label}
          {!saving&&<ChevronDown size={10}/>}
        </button>
        {open&&(
          <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:100,background:'#fff',border:`1px solid ${T.border}`,borderRadius:10,boxShadow:T.shadowMd,minWidth:165,overflow:'hidden'}}>
            {opts.map(s=>{
              const c=STATUS[s];
              if (!c) return null;
              return(
                <button key={s} onClick={()=>changeStatus(s)}
                  style={{display:'flex',alignItems:'center',gap:8,width:'100%',padding:'9px 14px',background:status===s?T.purpleBg:'transparent',border:'none',cursor:'pointer',fontFamily:T.font,fontSize:12,fontWeight:status===s?600:400,color:T.text,textAlign:'left'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:c.color,flexShrink:0}}/>
                  {c.label}
                  {status===s&&<Check size={11} color={T.purple} style={{marginLeft:'auto'}}/>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Rework Modal */}
      {showRework&&(
        <Modal title="Rework Request" subtitle="Describe what changes are needed" onClose={()=>setShowRework(false)}>
          <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'10px 14px',marginBottom:14}}>
            <p style={{fontSize:12,color:'#9a3412',fontWeight:500}}>⚠ This will notify the editor with your specific instructions.</p>
          </div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:6}}>What changes are needed? *</label>
          <textarea value={reworkNotes} onChange={e=>setReworkNotes(e.target.value)} rows={4}
            placeholder="Describe exactly what needs to be changed..." style={{...inputSt,resize:'vertical',lineHeight:1.7,marginBottom:14}}/>
          <div style={{display:'flex',justifyContent:'flex-end',gap:9}}>
            <Btn variant="secondary" onClick={()=>setShowRework(false)}>Cancel</Btn>
            <Btn variant="red" onClick={submitRework} disabled={saving}>{saving?'Sending...':'Send Rework Request'}</Btn>
          </div>
        </Modal>
      )}

      {/* Correction Modal — rare case after Ready to Upload */}
      {showCorrection&&(
        <Modal title="Correction Request" subtitle="Rare case — editor will be notified to fix" onClose={()=>setShowCorrection(false)}>
          <div style={{background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:10,padding:'10px 14px',marginBottom:14}}>
            <p style={{fontSize:12,color:'#92400e',fontWeight:500}}>⚠ This is a rare correction after Ready to Upload. Describe the specific fix needed.</p>
          </div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:6}}>What correction is needed? *</label>
          <textarea value={correctionNotes} onChange={e=>setCorrectionNotes(e.target.value)} rows={4}
            placeholder="Describe the specific correction needed..." style={{...inputSt,resize:'vertical',lineHeight:1.7,marginBottom:14}}/>
          <div style={{display:'flex',justifyContent:'flex-end',gap:9}}>
            <Btn variant="secondary" onClick={()=>setShowCorrection(false)}>Cancel</Btn>
            <Btn variant="red" onClick={submitCorrection} disabled={saving}>{saving?'Sending...':'Send Correction'}</Btn>
          </div>
        </Modal>
      )}
    </>
  );
}

/* ─── NOVA File Picker ────────────────────────────────────── */
function NOVAFilePicker({onSelect, onClose, multiple=false, videoOnly=false, initialFolderId=null, initialFolderPath=[], onFolderChange}) {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
const [currentFolder, setCurrentFolder] = useState(initialFolderId);
const [folderPath, setFolderPath] = useState(initialFolderPath);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sortMode, setSortMode] = useState('name_asc');

  const loadContents = useCallback(async (folderId) => {
    setLoading(true);
    try {
      const [fRes,fiRes] = await Promise.all([
        api.get('/folders',{params:{parent_id:folderId||'null'}}),
        api.get('/files',{params:{folder_id:folderId||'null'}}),
      ]);
      setFolders(fRes.data||[]);
      let fl=fiRes.data||[];
      if(videoOnly) fl=fl.filter(f=>['mp4','mov','avi','mkv','webm','m4v','mts','mxf'].includes(f.name?.split('.').pop()?.toLowerCase()));
      setFiles(fl);
    } catch { toast.error('Failed to load files'); }
    finally { setLoading(false); }
  },[videoOnly]);

  useEffect(()=>{loadContents(initialFolderId);},[loadContents]);

  const openFolder = f => {
  const newPath = [...folderPath, {id:f.id,name:f.name}];
  setCurrentFolder(f.id);
  setFolderPath(newPath);
  loadContents(f.id);
  onFolderChange?.(f.id, newPath);
};
  const goBack = () => {
  const p=folderPath.slice(0,-1);
  setFolderPath(p);
  const parent=p.length>0?p[p.length-1].id:null;
  setCurrentFolder(parent);
  loadContents(parent);
  onFolderChange?.(parent, p);
};
  const toggleSelect = f => { if(!multiple){setSelected([f]);return;} setSelected(prev=>prev.find(x=>x.id===f.id)?prev.filter(x=>x.id!==f.id):[...prev,f]); };

  const preview = async (file) => {
    setPreviewFile(file);
    try { const r=await api.get(`/files/${file.id}/preview-url`); setPreviewUrl(r.data); } catch { toast.error('Preview failed'); }
  };

  const confirm = () => {
    if(!selected.length) return toast.error('Select at least one file');
        const mapped=selected.map(f=>({id:f.id,name:f.name,r2_key:f.r2_key||null,bunny_video_id:f.bunny_video_id||null,preview_status:f.preview_status,folder_path:folderPath.map(fp=>fp.name).join(' / ')}));
    onSelect(multiple?mapped:mapped[0]);
    onClose();
  };

  const getIcon = name => {
    const ext=name?.split('.').pop()?.toLowerCase();
    if(['mp4','mov','avi','mkv','webm','m4v'].includes(ext)) return <Video size={14} color={T.purple}/>;
    return <FileText size={14} color={T.textSm}/>;
  };

  const sortItems = (items, isFolder) => {
  return [...items].sort((a,b)=>{
    if(sortMode==='name_asc')  return (a.name||'').localeCompare(b.name||'');
    if(sortMode==='name_desc') return (b.name||'').localeCompare(a.name||'');
    if(sortMode==='newest')    return new Date(b.created_at||0)-new Date(a.created_at||0);
    if(sortMode==='oldest')    return new Date(a.created_at||0)-new Date(b.created_at||0);
    if(!isFolder&&sortMode==='size_desc') return (b.size||0)-(a.size||0);
    if(!isFolder&&sortMode==='size_asc')  return (a.size||0)-(b.size||0);
    return 0;
  });
};
const sortedFolders = sortItems(folders, true);
const sortedFiles   = sortItems(files, false);

  return (
    <Modal title="Select from NOVA Drive" subtitle={videoOnly?'Video files only':'Select file'} onClose={onClose} wide
      actions={[<Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,<Btn key="s" onClick={confirm} disabled={!selected.length}><Check size={13}/>Select {selected.length>0?`(${selected.length})`:''}</Btn>]}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:14,flexWrap:'wrap'}}>
        <button onClick={()=>{setFolderPath([]);setCurrentFolder(null);loadContents(null);}}
          style={{background:'none',border:'none',cursor:'pointer',color:T.purple,fontSize:12,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:4}}>
          <Folder size={12}/>NOVA Drive
        </button>
        {folderPath.map((fp,i)=>(
          <React.Fragment key={fp.id}>
            <ChevronRight size={12} color={T.textXs}/>
            <button onClick={()=>{const p=folderPath.slice(0,i+1);setFolderPath(p);setCurrentFolder(fp.id);loadContents(fp.id);}}
              style={{background:'none',border:'none',cursor:'pointer',color:i===folderPath.length-1?T.text:T.purple,fontSize:12,fontFamily:T.font}}>{fp.name}</button>
          </React.Fragment>
        ))}
        {folderPath.length>0&&<button onClick={goBack} style={{marginLeft:'auto',background:T.purpleBg,border:'none',color:T.purpleText,cursor:'pointer',borderRadius:7,padding:'4px 10px',fontSize:11,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:4}}><ChevronLeft size={11}/>Back</button>}
        <select
      value={sortMode}
      onChange={e=>setSortMode(e.target.value)}
      style={{marginLeft:'auto',border:'1px solid #e8e4f3',borderRadius:7,padding:'4px 8px',fontSize:11,fontFamily:"'Poppins',sans-serif",color:'#4a3f6b',background:'#faf9ff',cursor:'pointer',outline:'none'}}
    >
      <option value="name_asc">Name A→Z</option>
      <option value="name_desc">Name Z→A</option>
      <option value="newest">Newest First</option>
      <option value="oldest">Oldest First</option>
      <option value="size_desc">Size ↓</option>
      <option value="size_asc">Size ↑</option>
    </select>
      </div>
      {loading?<div style={{display:'flex',justifyContent:'center',padding:36}}><Spinner/></div>:(
        <div style={{maxHeight:340,overflowY:'auto',border:`1px solid ${T.border}`,borderRadius:10}}>
          {folders.length===0&&files.length===0?<div style={{padding:24,textAlign:'center',color:T.textSm,fontSize:13}}>No files in this folder</div>:(
            <>
              {sortedFolders.map(folder=>(
                <div key={folder.id} onClick={()=>openFolder(folder)} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:`1px solid ${T.borderL}`,cursor:'pointer',background:'#faf9ff'}}>
                  <Folder size={15} color={T.yellow}/><span style={{fontSize:13,color:T.text,fontWeight:500,flex:1}}>{folder.name}</span><ChevronRight size={13} color={T.textXs}/>
                </div>
              ))}
              {sortedFiles.map(file=>{
                const isSel=!!selected.find(f=>f.id===file.id);
                const isVideo=['mp4','mov','avi','mkv','webm','m4v','mts','mxf'].includes(file.name?.split('.').pop()?.toLowerCase());
                return(
                  <div key={file.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',borderBottom:`1px solid ${T.borderL}`,cursor:'pointer',background:isSel?T.purpleBg:'transparent'}}>
                    <div onClick={()=>toggleSelect(file)} style={{display:'flex',alignItems:'center',gap:10,flex:1}}>
                      {file.bunny_video_id?<img src={`https://vz-78d09846-953.b-cdn.net/${file.bunny_video_id}/thumbnail.jpg`} style={{width:40,height:28,borderRadius:5,objectFit:'cover',flexShrink:0}} alt="thumb"/>:<div style={{width:40,height:28,borderRadius:5,background:T.purpleBg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{getIcon(file.name)}</div>}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,color:T.text,fontWeight:isSel?600:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{file.name}</div>
                        {folderPath.length>0&&<div style={{fontSize:10,color:T.textXs}}>{folderPath.map(fp=>fp.name).join(' / ')}</div>}
                      </div>
                      {isSel&&<Check size={14} color={T.purple}/>}
                    </div>
                    {isVideo&&(
  <button onClick={e=>{e.stopPropagation();preview(file);}} style={{background:T.purpleBg,border:'none',color:T.purple,cursor:'pointer',borderRadius:6,padding:'4px 8px',fontSize:10,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3,flexShrink:0}}>
    <Play size={10}/>Preview
  </button>
)}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
      {previewFile&&previewUrl&&(
        <div style={{marginTop:14,border:`1px solid ${T.border}`,borderRadius:10,overflow:'hidden'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',background:'#faf9ff',borderBottom:`1px solid ${T.border}`}}>
            <span style={{fontSize:12,fontWeight:600,color:T.text}}>{previewFile.name}</span>
            <button onClick={()=>{setPreviewFile(null);setPreviewUrl(null);}} style={{background:'none',border:'none',cursor:'pointer',color:T.textSm}}><X size={14}/></button>
          </div>
          {previewUrl.is_bunny?<iframe src={`${previewUrl.url}?autoplay=false`} style={{width:'100%',height:210,border:'none'}} allowFullScreen title="preview"/>:<video src={previewUrl.url} controls style={{width:'100%',maxHeight:210,background:'#000'}} playsInline/>}
        </div>
      )}
      {multiple&&selected.length>0&&<div style={{marginTop:10,padding:'8px 12px',background:T.purpleBg,borderRadius:9,fontSize:12,color:T.purpleText,fontWeight:500}}>{selected.length} file{selected.length>1?'s':''} selected: {selected.map(f=>f.name).join(', ')}</div>}
    </Modal>
  );
}

/* ─── EditedVideoCard with Watch + Download ──────────────── */
function EditedVideoCard({video, versions}) {
  const [watching, setWatching] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const watch = async () => {
    if(watching){setWatching(false);setPreviewUrl(null);return;}
    if(video.bunny_video_id&&video.preview_status==='ready'){
      setPreviewUrl({is_bunny:true,url:`https://iframe.mediadelivery.net/embed/641798/${video.bunny_video_id}?autoplay=false&responsive=true`});
      setWatching(true); return;
    }
    if(!video.id) return;
    setLoadingPreview(true);
    try { const r=await api.get(`/files/${video.id}/preview-url`); setPreviewUrl(r.data); setWatching(true); }
    catch { toast.error('Failed to load preview'); }
    finally { setLoadingPreview(false); }
  };

const download = async () => {
  try {
    toast('Preparing download...', { icon: '⏳', duration: 2000 });
    const r = await api.get(`/files/${video.id}/download`);
    const url = r.data?.download_url;
    if (!url) return toast.error('Download failed');
    const a = document.createElement('a');
    a.href = url;
    a.download = r.data?.filename || video.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch { toast.error('Download failed'); }
};

  return(
    <div style={{background:'#f0fdf4',border:'1px solid #6ee7b7',borderRadius:11,overflow:'hidden'}}>
      <div style={{padding:'12px 14px'}}>
        <div style={{fontWeight:700,fontSize:12,color:T.green,marginBottom:6,textTransform:'uppercase',letterSpacing:0.8}}>Edited Video</div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <Video size={14} color={T.green}/>
          <div style={{flex:1}}>
            <div style={{fontSize:12,color:T.text,fontWeight:600}}>{video.name}</div>
            {video.folder_path&&<div style={{fontSize:10,color:T.textXs}}>{video.folder_path}</div>}
          </div>
          <div style={{display:'flex',gap:6,flexShrink:0}}>
            {video.id&&<button onClick={download} style={{display:'flex',alignItems:'center',gap:4,background:'#d1fae5',border:'none',color:T.green,cursor:'pointer',borderRadius:7,padding:'5px 10px',fontSize:11,fontWeight:600,fontFamily:T.font}}><Download size={11}/>Download</button>}
            {(video.bunny_video_id||video.id)&&<button onClick={watch} disabled={loadingPreview} style={{display:'flex',alignItems:'center',gap:4,background:watching?'#fee2e2':'#1a1035',border:'none',color:'#fff',cursor:'pointer',borderRadius:7,padding:'5px 10px',fontSize:11,fontWeight:600,fontFamily:T.font}}>{loadingPreview?<Spinner size={11}/>:watching?<><X size={11}/>Close</>:<><Play size={11}/>Watch</>}</button>}
          </div>
        </div>
      </div>
      {watching&&previewUrl&&(
        <div style={{borderTop:'1px solid #6ee7b7',background:'#000'}}>
          {previewUrl.is_bunny?<iframe src={previewUrl.url} style={{width:'100%',aspectRatio:'16/9',border:'none',display:'block'}} allow="autoplay;fullscreen" allowFullScreen title={video.name}/>:<video src={previewUrl.url} controls style={{width:'100%',maxHeight:260,background:'#000',display:'block'}} playsInline/>}
        </div>
      )}
      {versions.length>0&&(
        <div style={{padding:'8px 14px',borderTop:'1px solid #6ee7b7',background:'rgba(255,255,255,0.5)'}}>
          <div style={{fontSize:10,color:T.textSm,fontWeight:600,marginBottom:4}}>Previous Versions ({versions.length})</div>
          {versions.map((v,i)=><div key={i} style={{fontSize:10,color:T.textXs,padding:'2px 0'}}>v{i+1}: {v.name} — {v.saved_at?new Date(v.saved_at).toLocaleDateString('en-IN'):''}</div>)}
        </div>
      )}
    </div>
  );
}


/* ─── CHANGE ASSIGNMENT MODAL ────────────────────────────── */
function ChangeAssignmentModal({task, type, editors, allUsers, onClose, onSaved}) {
  const currentId   = type==='editor' ? task.assigned_editor : task.assigned_smm;
  const currentName = type==='editor' ? (task.editor?.name||'Unassigned') : (task.smm?.name||'Unassigned');
  const list = type==='editor' ? editors : (allUsers||[]).filter(u=>u.role==='smm');
  const [selectedId, setSelectedId] = useState(currentId||'');
  const [saving, setSaving] = useState(false);

  const newName = list.find(u=>u.id===selectedId)?.name||'Unassigned';
  const changed = selectedId !== (currentId||'');

  const inputSt2 = {width:'100%',border:'1.5px solid #e8e4f3',borderRadius:9,padding:'9px 12px',fontSize:13,fontFamily:"'Poppins',sans-serif",color:'#1a1035',background:'#faf9ff',outline:'none',boxSizing:'border-box'};

  const submit = async () => {
    // Simple confirm — no reason needed
    if (!window.confirm(
      `Change ${type==='editor'?'Editor':'SMM'} from "${currentName}" to "${newName}"?\n\nThis will update the task immediately.`
    )) return;

    setSaving(true);
    try {
      const field = type==='editor' ? 'assigned_editor' : 'assigned_smm';
      await api.put(`/tasks/${task.id}`, {
        [field]: selectedId || null,
      });
      toast.success(`${type==='editor'?'Editor':'SMM'} changed to ${newName}`);
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error||'Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Change ${type==='editor'?'Editor':'SMM'}`}
      subtitle={`${task.task_code} · ${task.content_description}`}
      onClose={onClose}
      actions={[
        <Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,
        <Btn key="s" onClick={submit} disabled={saving||!changed}>
          {saving?'Saving...':'Confirm Change'}
        </Btn>
      ]}>

      {/* Current */}
      <div style={{background:'#f5f3ff',border:'1px solid #c4b5fd',borderRadius:10,padding:'10px 14px',marginBottom:14}}>
        <div style={{fontSize:11,color:'#8b7fb0',marginBottom:2}}>Currently assigned</div>
        <div style={{fontWeight:700,fontSize:14,color:'#7c3aed'}}>{currentName}</div>
      </div>

      {/* Select new */}
      <div>
        <label style={{display:'block',fontWeight:600,fontSize:12,color:'#4a3f6b',marginBottom:5}}>
          Select New {type==='editor'?'Editor':'SMM'} *
        </label>
        <select style={inputSt2} value={selectedId} onChange={e=>setSelectedId(e.target.value)}>
          <option value="">— Unassign</option>
          {list.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {/* Change preview */}
      {changed && (
        <div style={{background:'#d1fae5',border:'1px solid #6ee7b7',borderRadius:10,padding:'9px 14px',marginTop:12,fontSize:12,color:'#059669',fontWeight:600}}>
          ✓ {currentName} → {newName}
        </div>
      )}
    </Modal>
  );
}

/* ─── TASK ROW ────────────────────────────────────────────── */
function TaskRow({task, role, sno, onRefresh, onViewDetail, contentTypes, editors, showClientBadge, sharedFolderRef}) {
  const [editF, setEditF] = useState({});
  const [saving, setSaving] = useState(false);
  const [showNovaRaw, setShowNovaRaw] = useState(false);
  const [showNovaEdited, setShowNovaEdited] = useState(false);
  const [showApproveLink, setShowApproveLink] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {type, idx, name}
const [showColorPicker, setShowColorPicker] = useState(false);
const [changeAssignment, setChangeAssignment] = useState(null); // ← ADD
const [allUsers, setAllUsers] = useState([]);                   // ← ADD
  const colorRef = useRef();

  const today = new Date().toISOString().split('T')[0];
  const overdueFlag = isOverdue(task.due_date, task.status);
  const age = ageDays(task.created_at);
  const ageColor = age>4?T.red:age>2?T.yellow:null;

const canEdit = ['admin','smm'].includes(role);
const canEditAll = ['admin','smm'].includes(role); // SMM can now change editor/SMM

useEffect(()=>{
  if(canEditAll){
    api.get('/auth/users').then(r=>setAllUsers(r.data||[])).catch(()=>{});
  }
},[canEditAll]);

  const save = async (field, value) => {
    setSaving(true);
    try { await api.put(`/tasks/${task.id}`,{[field]:value}); onRefresh?.(); }
    catch(e) { toast.error(e.response?.data?.error||'Save failed'); }
    finally { setSaving(false); setEditF({}); }
  };

  const addRawFootage = async (files) => {
    const nf=Array.isArray(files)?files:[files];
    await save('raw_footage',[...(task.raw_footage||[]),...nf]);
  };

  const confirmDelete = (type, idx, name) => setDeleteConfirm({type,idx,name});

  const handleDelete = async () => {
    const {type,idx} = deleteConfirm;
    if(type==='raw') {
      const updated=(task.raw_footage||[]).filter((_,i)=>i!==idx);
      await save('raw_footage',updated);
    } else if(type==='edited') {
      await save('edited_video',null);
    } else if(type==='cta') {
      await save('cta_link',null);
      await save('cta','no');
    }
    setDeleteConfirm(null);
  };

const getCycleApprovalLink = async () => {
  try {
    const r = await api.post(`/tasks/${task.id}/approve-link`);
    setShowApproveLink(r.data.approval_link);
    // No WhatsApp redirect — admin/SMM shares manually
    toast.success('Approval link ready! Copy and share with the client.');
    onRefresh?.();
  } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
};

const downloadFile = async (fileId, fileName) => {
  try {
    toast('Preparing download...', { icon: '⏳', duration: 2000 });
    const r = await api.get(`/files/${fileId}/download`);
    const url = r.data?.download_url;
    if (!url) return toast.error('Download failed');
    const a = document.createElement('a');
    a.href = url;
    a.download = r.data?.filename || fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch { toast.error('Download failed'); }
};

  const cell = {padding:'0 8px',borderRight:`1px solid ${T.borderL}`,display:'flex',alignItems:'center',minHeight:52,fontSize:12,color:T.textMd,position:'relative'};

  // Row background
  const rowBg = overdueFlag?'#fff5f5':['rework','client_rework','correction'].includes(task.status)?'#fff7ed':['client_approved','ready_to_upload'].includes(task.status)?'#f0fdf4':task.status==='uploaded'?'#f0fdf4':T.surface;

  return(
    <>
      <div style={{
        display:'grid',
        gridTemplateColumns:'10px 40px 40px 1.6fr 130px 110px 110px 140px 55px 150px 140px 110px 120px',
        borderBottom:`1px solid ${T.borderL}`,
        background:rowBg,
        transition:'background 0.15s',
        minWidth:1380,
      }}>

        {/* COLOR BAR */}
        <div style={{...cell,padding:0,borderRight:`1px solid ${T.borderL}`,position:'relative'}}>
          <div onClick={()=>canEdit&&setShowColorPicker(s=>!s)}
            style={{width:'100%',height:'100%',background:task.color_bar||'#1868f0',cursor:canEdit?'pointer':'default',minHeight:52}}/>
          {canEdit&&showColorPicker&&(
            <ColorPicker value={task.color_bar||'#1868f0'} onChange={c=>save('color_bar',c)} onClose={()=>setShowColorPicker(false)}/>
          )}
        </div>

        {/* S.NO */}
        <div style={{...cell,justifyContent:'center',fontWeight:700,fontSize:12,color:T.textXs}}>
          {sno}
          {ageColor&&<div style={{position:'absolute',top:4,right:4,width:6,height:6,borderRadius:'50%',background:ageColor}}/>}
        </div>

        {/* Actions */}
        <div style={{...cell,justifyContent:'center',gap:4,flexDirection:'column'}}>
          <button onClick={()=>onViewDetail(task)} style={{background:T.purpleBg,border:'none',color:T.purpleText,cursor:'pointer',borderRadius:6,width:26,height:26,display:'flex',alignItems:'center',justifyContent:'center'}} title="View Detail"><Eye size={12}/></button>
         {canEditAll&&<button onClick={async()=>{
  if(!window.confirm('Delete this task?')) return;
  try {
    await api.delete(`/tasks/${task.id}`);
    toast.success('Task deleted');
    onRefresh?.();
  } catch(e) {
    toast.error(e.response?.data?.error||'Failed to delete task');
  }
}} style={{background:'#fee2e2',border:'none',color:T.red,cursor:'pointer',borderRadius:6,width:26,height:26,display:'flex',alignItems:'center',justifyContent:'center'}} title="Delete"><Trash2 size={11}/></button>}
        </div>

        {/* Content Description */}
        <div style={{...cell,flexDirection:'column',alignItems:'flex-start',gap:2,padding:'6px 8px'}}>
          {editF.desc?(
            <input autoFocus defaultValue={task.content_description} style={{...inputSt,fontSize:12,padding:'4px 7px'}}
              onBlur={e=>save('content_description',e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter')save('content_description',e.target.value);if(e.key==='Escape')setEditF({});}}/>
          ):(
            <div onClick={()=>canEdit&&setEditF({desc:true})} style={{cursor:canEdit?'text':'default',fontWeight:500,color:T.text,fontSize:12,lineHeight:1.4,width:'100%'}}>{task.content_description}</div>
          )}
          <div style={{display:'flex',gap:5,flexWrap:'wrap',alignItems:'center'}}>
            {task.rework_count>0&&<span style={{background:'#fee2e2',color:T.red,fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:10}}>Rework ×{task.rework_count}</span>}
            {task.task_code&&<span style={{fontSize:9,color:T.textXs}}>{task.task_code}</span>}
            {showClientBadge&&task.client_name&&<span style={{fontSize:9,color:T.purpleText,background:T.purpleBg,padding:'1px 6px',borderRadius:8}}>{task.client_name}</span>}
          </div>
        </div>

        {/* Status */}
        <div style={{...cell,justifyContent:'center'}}>
          <StatusChip status={task.status} taskId={task.id} role={role} onChanged={onRefresh}/>
        </div>

        {/* Editor */}
{/* Editor */}
<div style={{...cell}}>
  <div onClick={()=>canEditAll&&setChangeAssignment({type:'editor'})}
    style={{cursor:canEditAll?'pointer':'default',display:'flex',alignItems:'center',gap:6,width:'100%'}}>
    {task.editor?.name?(
      <>
        <div style={{width:22,height:22,borderRadius:6,background:'#ede9fe',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color:'#6d28d9',flexShrink:0}}>
          {task.editor.name[0]}
        </div>
        <span style={{fontSize:11,color:'#1a1035',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{task.editor.name}</span>
        {canEditAll&&<span style={{fontSize:9,color:'#b8b3cc',marginLeft:'auto',flexShrink:0}}>✏</span>}
      </>
    ):(
      <span style={{fontSize:11,color:canEditAll?'#7c3aed':'#b8b3cc',fontWeight:canEditAll?600:400}}>
        {canEditAll?'+ Assign':'—'}
      </span>
    )}
  </div>
</div>

        {/* Content Type */}
        <div style={{...cell}}>
          {canEdit&&editF.ct?(
            <select autoFocus defaultValue={task.content_type_id||''} style={{...inputSt,fontSize:11,padding:'3px 6px'}}
              onChange={e=>{const ct=contentTypes.find(c=>c.id===e.target.value);if(ct){api.put(`/tasks/${task.id}`,{content_type_id:ct.id,content_type_name:ct.name}).then(()=>onRefresh?.());}setEditF({});}}>
              <option value="">— Select</option>
              {contentTypes.map(ct=><option key={ct.id} value={ct.id}>{ct.name}</option>)}
            </select>
          ):(
            <div onClick={()=>canEdit&&setEditF({ct:true})} style={{cursor:canEdit?'pointer':'default'}}>
              {task.content_type?<span style={{background:task.content_type.color+'22',color:task.content_type.color,fontSize:10,fontWeight:600,padding:'3px 8px',borderRadius:12,border:`1px solid ${task.content_type.color}44`}}>{task.content_type.name}</span>:<span style={{fontSize:11,color:T.textXs}}>— Type</span>}
            </div>
          )}
        </div>

        {/* Raw Footage */}
        <div style={{...cell,flexDirection:'column',alignItems:'flex-start',gap:3,padding:'6px 8px'}}>
          {(task.raw_footage||[]).map((f,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:4,background:'#f5f3ff',borderRadius:6,padding:'2px 6px',maxWidth:'100%'}}>
              <Video size={9} color={T.purple}/>
              <span style={{fontSize:9,color:T.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:75,cursor:'pointer'}} onClick={()=>f.id&&downloadFile(f.id,f.name)} title="Download">{f.name}</span>
              {(canEdit||role==='editor')&&<button onClick={()=>confirmDelete('raw',i,f.name)} style={{background:'none',border:'none',cursor:'pointer',color:T.red,padding:0,display:'flex'}}><X size={8}/></button>}
            </div>
          ))}
          {(canEdit||role==='editor')&&<button onClick={()=>setShowNovaRaw(true)} style={{background:T.purpleBg,border:'1px dashed #c4b5fd',color:T.purpleText,cursor:'pointer',borderRadius:6,padding:'2px 7px',fontSize:9,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3}}><Plus size={9}/>NOVA</button>}
        </div>

        {/* CTA */}
        <div style={{...cell,justifyContent:'center'}}>
          {canEdit?(
            <select value={task.cta||'no'} onChange={e=>save('cta',e.target.value)}
              style={{border:'none',background:'transparent',fontSize:11,fontWeight:600,color:task.cta==='yes'?T.green:T.textSm,cursor:'pointer',fontFamily:T.font,outline:'none',textAlign:'center'}}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          ):<span style={{fontSize:11,fontWeight:600,color:task.cta==='yes'?T.green:T.textSm}}>{task.cta==='yes'?'Yes':'No'}</span>}
        </div>

        {/* CTA Link (replaces document upload) */}
        <div style={{...cell,flexDirection:'column',alignItems:'flex-start',gap:3,padding:'6px 8px'}}>
          {task.cta==='yes'?(
            <>
              {task.cta_link?(
                <div style={{display:'flex',alignItems:'center',gap:4,maxWidth:'100%'}}>
                  <a href={task.cta_link} target="_blank" rel="noreferrer" style={{display:'flex',alignItems:'center',gap:4,background:'#dbeafe',borderRadius:6,padding:'2px 7px',textDecoration:'none',maxWidth:'100%'}}>
                    <Link size={9} color={T.blue}/>
                    <span style={{fontSize:9,color:T.blue,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:80}}>Drive Link</span>
                    <ExternalLink size={8} color={T.blue}/>
                  </a>
                  {canEdit&&<button onClick={()=>confirmDelete('cta',0,'CTA Link')} style={{background:'none',border:'none',cursor:'pointer',color:T.red,padding:0,display:'flex'}}><X size={8}/></button>}
                </div>
              ):(
                canEdit&&(
                  editF.cta_input?(
                    <input autoFocus style={{...inputSt,fontSize:10,padding:'3px 6px',width:120}}
                      placeholder="Paste Drive URL..."
                      onBlur={e=>{if(e.target.value.trim())save('cta_link',e.target.value.trim());setEditF({});}}
                      onKeyDown={e=>{if(e.key==='Enter'&&e.target.value.trim())save('cta_link',e.target.value.trim());if(e.key==='Escape')setEditF({});}}/>
                  ):(
                    <button onClick={()=>setEditF({cta_input:true})} style={{background:'#dbeafe',border:'1px dashed #93c5fd',color:T.blue,cursor:'pointer',borderRadius:6,padding:'2px 7px',fontSize:9,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3}}><Link size={9}/>Add Link</button>
                  )
                )
              )}
            </>
          ):<span style={{fontSize:10,color:T.textXs}}>—</span>}
        </div>

        {/* Edited Video */}
        <div style={{...cell,flexDirection:'column',alignItems:'flex-start',gap:3,padding:'6px 8px'}}>
          {task.edited_video?(
            <div style={{display:'flex',alignItems:'center',gap:4,background:'#d1fae5',borderRadius:6,padding:'2px 6px',maxWidth:'100%'}}>
              <Video size={9} color={T.green}/>
              <span style={{fontSize:9,color:T.green,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:75,cursor:'pointer'}} onClick={()=>task.edited_video.id&&downloadFile(task.edited_video.id,task.edited_video.name)} title="Download">{task.edited_video.name}</span>
              {(canEdit||role==='editor')&&<button onClick={()=>confirmDelete('edited',0,task.edited_video.name)} style={{background:'none',border:'none',cursor:'pointer',color:T.red,padding:0,display:'flex'}}><X size={8}/></button>}
            </div>
          ):null}
          {(canEdit||role==='editor')&&<button onClick={()=>setShowNovaEdited(true)} style={{background:'#d1fae5',border:'1px dashed #6ee7b7',color:T.green,cursor:'pointer',borderRadius:6,padding:'2px 7px',fontSize:9,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3}}><Plus size={9}/>NOVA</button>}
          {canEdit&&task.edited_video&&['review','rework','correction'].includes(task.status)&&(
            <button onClick={getCycleApprovalLink} style={{background:T.blueBg,border:'1px solid #93c5fd',color:T.blue,cursor:'pointer',borderRadius:6,padding:'2px 7px',fontSize:9,fontWeight:600,fontFamily:T.font,display:'flex',alignItems:'center',gap:3,marginTop:2}}><Send size={9}/>Send to Client</button>
          )}
        </div>

        {/* Due Date */}
        <div style={{...cell}}>
          {canEdit&&editF.due?(
            <input type="date" autoFocus defaultValue={task.due_date||''} style={{...inputSt,fontSize:11,padding:'3px 6px'}}
              onBlur={e=>save('due_date',e.target.value||null)}
              onKeyDown={e=>{if(e.key==='Enter')save('due_date',e.target.value||null);if(e.key==='Escape')setEditF({});}}/>
          ):(
            <div onClick={()=>canEdit&&setEditF({due:true})} style={{cursor:canEdit?'pointer':'default',display:'flex',alignItems:'center',gap:5}}>
              {task.due_date?<span style={{fontSize:11,fontWeight:500,color:overdueFlag?T.red:T.text}}>{overdueFlag&&<AlertTriangle size={10} color={T.red} style={{marginRight:3}}/>}{fmtDate(task.due_date)}</span>:<span style={{fontSize:11,color:T.textXs}}>— Set date</span>}
            </div>
          )}
        </div>

        {/* Client Approval Status */}
        <div style={{...cell,justifyContent:'center'}}>
          {task.status==='client_approved'?(<div style={{textAlign:'center'}}><div style={{fontSize:10,fontWeight:700,color:T.green}}>✓ Approved</div>{task.client_approved_at&&<div style={{fontSize:9,color:T.textXs}}>{fmtDate(task.client_approved_at.split('T')[0])}</div>}</div>)
          :task.status==='client_rework'?(<div style={{textAlign:'center'}}><div style={{fontSize:10,fontWeight:700,color:T.orange}}>⟳ Rework</div>{task.client_approval_notes&&<div style={{fontSize:9,color:T.textXs,maxWidth:80,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{task.client_approval_notes}</div>}</div>)
          :task.status==='client_approval'?(<div style={{fontSize:10,fontWeight:600,color:T.blue}}>⏳ Pending</div>)
          :task.status==='ready_to_upload'?(<div style={{fontSize:10,fontWeight:600,color:'#4f46e5'}}>📤 Upload Now</div>)
          :task.status==='uploaded'?(<div style={{fontSize:10,fontWeight:700,color:'#16a34a'}}>✓ Done</div>)
          :null}
        </div>

      </div>

      {/* NOVA pickers */}
{showNovaRaw&&(
  <NOVAFilePicker
    multiple videoOnly
    initialFolderId={sharedFolderRef.current.folderId}
    initialFolderPath={sharedFolderRef.current.folderPath}
    onFolderChange={(fid,fp)=>{ sharedFolderRef.current={folderId:fid,folderPath:fp}; }}
    onSelect={addRawFootage}
    onClose={()=>setShowNovaRaw(false)}
  />
)}
{showNovaEdited&&(
  <NOVAFilePicker
    videoOnly
    initialFolderId={sharedFolderRef.current.folderId}
    initialFolderPath={sharedFolderRef.current.folderPath}
    onFolderChange={(fid,fp)=>{ sharedFolderRef.current={folderId:fid,folderPath:fp}; }}
    onSelect={f=>save('edited_video',f)}
    onClose={()=>setShowNovaEdited(false)}
  />
)}

      {/* Delete confirmation */}
      {deleteConfirm&&<ConfirmDeleteModal fileName={deleteConfirm.name} onConfirm={handleDelete} onCancel={()=>setDeleteConfirm(null)}/>}

        {/* Change Assignment Modal */}
{changeAssignment&&(
  <ChangeAssignmentModal
    task={task}
    type={changeAssignment.type}
    editors={editors}
    allUsers={allUsers}
    onClose={()=>setChangeAssignment(null)}
    onSaved={()=>{setChangeAssignment(null);onRefresh?.();}}
  />
)}

      {/* Approval link popup */}
      {showApproveLink&&(
        <Modal title="Client Approval Link" subtitle="Share this link with the client" onClose={()=>setShowApproveLink(null)}>
          <div style={{background:T.purpleBg,border:'1px solid #c4b5fd',borderRadius:10,padding:'12px 14px',marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:600,color:T.purpleText,marginBottom:6}}>Shareable Link:</div>
            <div style={{fontSize:12,color:T.text,wordBreak:'break-all',fontFamily:'monospace'}}>{showApproveLink}</div>
          </div>
          <Btn full onClick={()=>{navigator.clipboard.writeText(showApproveLink);toast.success('Copied!');}}><Check size={13}/>Copy Link</Btn>
        </Modal>
      )}
    </>
  );
}

/* ─── Raw Footage Item with Watch + Download ─────────────── */
function RawFootageItem({file:f, onDownload}) {
  const [watching, setWatching] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const isVideo = ['mp4','mov','avi','mkv','webm','m4v','mts','mxf']
    .includes(f.name?.split('.').pop()?.toLowerCase());

  const watch = async () => {
    if (watching) { setWatching(false); setPreviewUrl(null); return; }
    if (!f.id) return;
    // Check bunny first
    if (f.bunny_video_id && f.preview_status === 'ready') {
      setPreviewUrl({
        is_bunny: true,
        url: `https://iframe.mediadelivery.net/embed/641798/${f.bunny_video_id}?autoplay=false&responsive=true`
      });
      setWatching(true); return;
    }
    setLoadingPreview(true);
    try {
      const r = await api.get(`/files/${f.id}/preview-url`);
      setPreviewUrl(r.data);
      setWatching(true);
    } catch { toast.error('Preview failed'); }
    finally { setLoadingPreview(false); }
  };

  return (
    <div style={{borderBottom:`1px solid ${T.borderL}`,paddingBottom:8,marginBottom:8}}>
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0'}}>
        <Video size={13} color={T.purple}/>
        <div style={{flex:1}}>
          <div style={{fontSize:12,color:T.text,fontWeight:500}}>{f.name}</div>
          {f.folder_path&&<div style={{fontSize:10,color:T.textXs}}>{f.folder_path}</div>}
        </div>
        <div style={{display:'flex',gap:6,flexShrink:0}}>
          {f.id && isVideo && (
            <button onClick={watch} disabled={loadingPreview}
              style={{display:'flex',alignItems:'center',gap:4,background:watching?'#fee2e2':'#1a1035',border:'none',color:'#fff',cursor:'pointer',borderRadius:7,padding:'5px 10px',fontSize:11,fontWeight:600,fontFamily:T.font}}>
              {loadingPreview ? <Spinner size={11}/> : watching ? <><X size={11}/>Close</> : <><Play size={11}/>Watch</>}
            </button>
          )}
          {f.id && (
            <button onClick={()=>onDownload(f.id,f.name)}
              style={{display:'flex',alignItems:'center',gap:4,background:T.purpleBg,border:'none',color:T.purple,cursor:'pointer',borderRadius:7,padding:'5px 10px',fontSize:11,fontWeight:600,fontFamily:T.font}}>
              <Download size={11}/>Download
            </button>
          )}
        </div>
      </div>
      {watching && previewUrl && (
        <div style={{borderRadius:8,overflow:'hidden',marginTop:6,background:'#000'}}>
          {previewUrl.is_bunny
            ? <iframe src={previewUrl.url} style={{width:'100%',aspectRatio:'16/9',border:'none',display:'block'}} allow="autoplay;fullscreen" allowFullScreen title={f.name}/>
            : <video src={previewUrl.url} controls style={{width:'100%',maxHeight:220,background:'#000',display:'block'}} playsInline/>
          }
        </div>
      )}
    </div>
  );
}

/* ─── TASK DETAIL PANEL ──────────────────────────────────── */
function TaskDetailPanel({task:initialTask, onClose, onRefresh, role}) {
  const [task, setTask] = useState(initialTask);
  const [history, setHistory] = useState([]);
  const [reworkNotes, setReworkNotes] = useState([]);
  const [activeTab, setActiveTab] = useState('detail');
  const [approvalLink, setApprovalLink] = useState(null);

  useEffect(()=>{
    api.get(`/tasks/${task.id}`).then(r=>setTask(r.data)).catch(()=>{});
    api.get(`/tasks/${task.id}/history`).then(r=>setHistory(r.data)).catch(()=>{});
    api.get(`/tasks/${task.id}/rework-notes`).then(r=>setReworkNotes(r.data)).catch(()=>{});
    if(task.client_approval_token) setApprovalLink(`${window.location.origin}/review/${task.client_approval_token}`);
  },[task.id]);

  const cfg = STATUS[task.status]||STATUS.ready_to_edit;

const downloadFile = async (fileId, fileName) => {
  try {
    toast('Preparing download...', { icon: '⏳', duration: 2000 });
    const r = await api.get(`/files/${fileId}/download`);
    const url = r.data?.download_url;
    if (!url) return toast.error('Download failed');
    const a = document.createElement('a');
    a.href = url;
    a.download = r.data?.filename || fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch { toast.error('Download failed'); }
};

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'stretch',justifyContent:'flex-end',zIndex:2500,backdropFilter:'blur(2px)'}}>
      <div style={{width:490,background:'#fff',boxShadow:'-4px 0 24px rgba(0,0,0,0.12)',display:'flex',flexDirection:'column',fontFamily:T.font,overflowY:'auto'}}>

        {/* Header */}
        <div style={{padding:'18px 20px',borderBottom:`1px solid ${T.border}`,background:T.purpleBg}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div style={{flex:1,marginRight:12}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <ColorBar color={task.color_bar||'#1868f0'} size={14}/>
                <span style={{fontSize:10,fontWeight:600,color:T.purple}}>{task.task_code} · {task.client_name}</span>
              </div>
              <div style={{fontWeight:700,fontSize:16,color:T.text,lineHeight:1.3}}>{task.content_description}</div>
              {task.rework_count>0&&<div style={{marginTop:5}}><span style={{background:'#fee2e2',color:T.red,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10}}>Rework ×{task.rework_count}</span></div>}
            </div>
            <button onClick={onClose} style={{background:'white',border:`1px solid ${T.border}`,cursor:'pointer',borderRadius:8,padding:6,display:'flex'}}><X size={15}/></button>
          </div>
          <div style={{marginTop:10,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{background:cfg.bg,border:`1px solid ${cfg.border}`,color:cfg.color,fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20}}>{cfg.label}</span>
            {task.content_type&&<span style={{background:task.content_type.color+'22',color:task.content_type.color,fontSize:10,fontWeight:600,padding:'3px 8px',borderRadius:12}}>{task.content_type.name}</span>}
            {task.due_date&&<span style={{fontSize:11,color:isOverdue(task.due_date,task.status)?T.red:T.textMd,fontWeight:500}}>📅 {fmtDate(task.due_date)}</span>}
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:'flex',borderBottom:`1px solid ${T.border}`}}>
          {[{id:'detail',l:'Details'},{id:'rework',l:`Reworks (${reworkNotes.length})`},{id:'history',l:'History'}].map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{flex:1,padding:'10px',background:'none',border:'none',borderBottom:activeTab===t.id?`2px solid ${T.purple}`:'2px solid transparent',marginBottom:-1,fontSize:12,fontWeight:activeTab===t.id?600:400,color:activeTab===t.id?T.purple:T.textSm,cursor:'pointer',fontFamily:T.font}}>
              {t.l}
            </button>
          ))}
        </div>

        <div style={{flex:1,overflow:'auto',padding:'16px 20px'}}>

          {activeTab==='detail'&&(
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {/* Assignments */}
              <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:11,padding:'12px 14px'}}>
                <div style={{fontWeight:700,fontSize:12,color:T.text,marginBottom:8,textTransform:'uppercase',letterSpacing:0.8}}>Assignments</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <div><div style={{fontSize:10,color:T.textXs,marginBottom:3}}>SMM</div><div style={{fontWeight:600,fontSize:13,color:T.text}}>{task.smm?.name||'—'}</div></div>
                  <div><div style={{fontSize:10,color:T.textXs,marginBottom:3}}>Editor</div><div style={{fontWeight:600,fontSize:13,color:T.text}}>{task.editor?.name||'—'}</div></div>
                </div>
                {task.editing_started_at&&<div style={{marginTop:8,fontSize:11,color:T.textSm}}>✏ Editing started: {new Date(task.editing_started_at).toLocaleString('en-IN')}</div>}
              </div>

              {/* Raw Footage */}
              {(task.raw_footage||[]).length>0&&(
                <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:11,padding:'12px 14px'}}>
                  <div style={{fontWeight:700,fontSize:12,color:T.text,marginBottom:8,textTransform:'uppercase',letterSpacing:0.8}}>Raw Footage ({task.raw_footage.length})</div>
                  {task.raw_footage.map((f,i)=>(
  <RawFootageItem key={i} file={f} onDownload={downloadFile}/>
))}
                </div>
              )}

              {/* Edited Video */}
              {task.edited_video&&<EditedVideoCard video={task.edited_video} versions={task.edited_video_versions||[]}/>}

              {/* CTA Link */}
              {task.cta==='yes'&&task.cta_link&&(
                <div style={{background:T.blueBg,border:'1px solid #93c5fd',borderRadius:11,padding:'12px 14px'}}>
                  <div style={{fontWeight:700,fontSize:12,color:T.blue,marginBottom:6,textTransform:'uppercase',letterSpacing:0.8}}>CTA Drive Link</div>
                  <a href={task.cta_link} target="_blank" rel="noreferrer" style={{display:'flex',alignItems:'center',gap:8,textDecoration:'none'}}>
                    <Link size={14} color={T.blue}/>
                    <span style={{fontSize:12,color:T.blue,fontWeight:500,wordBreak:'break-all'}}>{task.cta_link}</span>
                    <ExternalLink size={12} color={T.blue} style={{flexShrink:0}}/>
                  </a>
                </div>
              )}

              {/* Approval Link */}
              {approvalLink&&(
                <div style={{background:T.purpleBg,border:'1px solid #c4b5fd',borderRadius:11,padding:'12px 14px'}}>
                  <div style={{fontWeight:700,fontSize:12,color:T.purpleText,marginBottom:6}}>Client Review Link</div>
                  <div style={{fontSize:11,color:T.text,wordBreak:'break-all',marginBottom:8,fontFamily:'monospace'}}>{approvalLink}</div>
                  <div style={{display:'flex',gap:7}}>
                    <Btn small onClick={()=>{navigator.clipboard.writeText(approvalLink);toast.success('Copied!');}}><Check size={11}/>Copy</Btn>
                    <Btn small variant="secondary" onClick={()=>window.open(approvalLink,'_blank')}><ExternalLink size={11}/>Open</Btn>
                  </div>
                </div>
              )}

              {/* Client Approved */}
              {task.client_approved_at&&(
                <div style={{background:'#f0fdf4',border:'1px solid #6ee7b7',borderRadius:11,padding:'12px 14px'}}>
                  <div style={{fontWeight:700,fontSize:12,color:T.green,marginBottom:4}}>✓ Client Approved</div>
                  <div style={{fontSize:12,color:T.textMd}}>By: {task.client_approved_by_name||'Client'}</div>
                  <div style={{fontSize:11,color:T.textXs}}>{new Date(task.client_approved_at).toLocaleString('en-IN')}</div>
                </div>
              )}
            </div>
          )}

          {activeTab==='rework'&&(
            <div>
              {!reworkNotes.length?<div style={{textAlign:'center',padding:28,color:T.textSm,fontSize:13}}>No rework requests yet</div>:(
                reworkNotes.map(note=>(
                  <div key={note.id} style={{background:note.rework_type==='client'?'#fff7ed':note.rework_type==='correction'?'#fef3c7':'#fff5f5',border:`1px solid ${note.rework_type==='client'?'#fed7aa':note.rework_type==='correction'?'#fcd34d':'#fca5a5'}`,borderRadius:10,padding:'12px 14px',marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                      <span style={{fontWeight:700,fontSize:12,color:note.rework_type==='client'?T.orange:note.rework_type==='correction'?T.amber:T.red}}>
                        {note.rework_type==='client'?'👤 Client Rework':note.rework_type==='correction'?'⚙ Correction':'🔄 Rework'} #{note.rework_number}
                      </span>
                      <span style={{fontSize:10,color:T.textXs}}>{new Date(note.created_at).toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{fontSize:13,color:T.text,lineHeight:1.6}}>{note.notes}</div>
                    <div style={{fontSize:11,color:T.textSm,marginTop:4}}>By: {note.requester_name||note.requested_by}</div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab==='history'&&(
            <div>
              {!history.length?<div style={{textAlign:'center',padding:28,color:T.textSm,fontSize:13}}>No history yet</div>:(
                history.map(h=>(
                  <div key={h.id} style={{display:'flex',gap:10,padding:'10px 0',borderBottom:`1px solid ${T.borderL}`}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:T.purple,marginTop:4,flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,color:T.text,fontWeight:500}}>
                        {h.from_status&&<span style={{color:T.textSm}}>{STATUS[h.from_status]?.label||h.from_status} → </span>}
                        <span style={{fontWeight:600}}>{STATUS[h.to_status]?.label||h.to_status}</span>
                      </div>
                      {h.notes&&<div style={{fontSize:11,color:T.textMd,marginTop:2}}>{h.notes}</div>}
                      <div style={{fontSize:10,color:T.textXs,marginTop:2}}>{h.user?.name||'System'} · {new Date(h.changed_at).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── ADD TASK MODAL ──────────────────────────────────────── */
function AddTaskModal({clientId, clientName, editors, contentTypes, onClose, onSaved}) {
  const [form, setForm] = useState({content_description:'',assigned_editor:'',content_type_id:'',cta:'no',cta_link:'',due_date:'',notes:'',color_bar:'#1868f0'});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const ct=contentTypes.find(c=>c.id===form.content_type_id);
      await api.post('/tasks',{...form,client_id:clientId,client_name:clientName,content_type_name:ct?.name||null,assigned_editor:form.assigned_editor||null,cta_link:form.cta==='yes'&&form.cta_link?form.cta_link:null});
      toast.success('Task created');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  return(
    <Modal title="Add New Task" subtitle={`Client: ${clientName}`} onClose={onClose}
      actions={[<Btn key="c" variant="secondary" onClick={onClose}>Cancel</Btn>,<Btn key="s" onClick={submit} disabled={saving}>{saving?'Creating...':'Create Task'}</Btn>]}>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Content Description</label>
          <input style={inputSt} value={form.content_description} onChange={e=>setForm(p=>({...p,content_description:e.target.value}))} placeholder="e.g. Baldness Surgery — Informational Video"/>
        </div>

        {/* Priority color */}
        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:8}}>Priority Color</label>
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            {PRIORITY_COLORS.map(p=>(
              <div key={p.color} onClick={()=>setForm(f=>({...f,color_bar:p.color}))}
                style={{width:28,height:28,borderRadius:7,background:p.color,cursor:'pointer',border:form.color_bar===p.color?`3px solid ${T.text}`:'2px solid transparent',transition:'all 0.1s'}} title={p.label}/>
            ))}
            <span style={{fontSize:11,color:T.textSm}}>{PRIORITY_COLORS.find(p=>p.color===form.color_bar)?.label||'Normal'}</span>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Assign Editor</label>
            <select style={inputSt} value={form.assigned_editor} onChange={e=>setForm(p=>({...p,assigned_editor:e.target.value}))}>
              <option value="">— Select Editor</option>
              {editors.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Content Type</label>
            <select style={inputSt} value={form.content_type_id} onChange={e=>setForm(p=>({...p,content_type_id:e.target.value}))}>
              <option value="">— Select Type</option>
              {contentTypes.map(ct=><option key={ct.id} value={ct.id}>{ct.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Due Date <span style={{fontWeight:400,color:T.textXs}}>(optional — editor sees task only after this is set)</span></label>
            <input type="date" style={inputSt} value={form.due_date} onChange={e=>setForm(p=>({...p,due_date:e.target.value}))}/>
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>CTA Required</label>
            <select style={inputSt} value={form.cta} onChange={e=>setForm(p=>({...p,cta:e.target.value}))}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
        </div>

        {form.cta==='yes'&&(
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Google Drive / Docs Link</label>
            <input style={inputSt} value={form.cta_link} onChange={e=>setForm(p=>({...p,cta_link:e.target.value}))} placeholder="https://docs.google.com/..."/>
          </div>
        )}

        <div>
          <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>Notes</label>
          <textarea style={{...inputSt,resize:'vertical',lineHeight:1.6}} rows={2} value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Any additional notes..."/>
        </div>
      </div>
    </Modal>
  );
}


/* ════════════════════════════════════════════════════════════
   PHASE 4 — MASTER REPORTS SECTION
   Admin/SMM: full table with all filters
   Editor: own simplified view only
════════════════════════════════════════════════════════════ */
function MasterReportsSection({role, userId, editors, clients}) {

  // ── Date range presets ──────────────────────────────────
  const getPresetRange = (preset) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    if(preset==='today') return {from:today, to:today};
    if(preset==='week'){
      const start=new Date(now); start.setDate(now.getDate()-7);
      return {from:start.toISOString().split('T')[0], to:today};
    }
    if(preset==='month'){
      const start=new Date(now.getFullYear(),now.getMonth(),1);
      return {from:start.toISOString().split('T')[0], to:today};
    }
    if(preset==='last_month'){
      const start=new Date(now.getFullYear(),now.getMonth()-1,1);
      const end=new Date(now.getFullYear(),now.getMonth(),0);
      return {from:start.toISOString().split('T')[0], to:end.toISOString().split('T')[0]};
    }
    return {from:'', to:''};
  };

  // ── State ────────────────────────────────────────────────
  const [preset,setPreset] = useState('month');
  const [dateFrom,setDateFrom] = useState(()=>getPresetRange('month').from);
  const [dateTo,setDateTo] = useState(()=>getPresetRange('month').to);
  const [filterEditor,setFilterEditor] = useState('');
  const [filterClient,setFilterClient] = useState('');
  const [filterStatus,setFilterStatus] = useState('');
  const [filterColor,setFilterColor] = useState('');
  const [sortCol,setSortCol] = useState('created_at');
  const [sortDir,setSortDir] = useState('desc');
  const [tasks,setTasks] = useState([]);
  const [summary,setSummary] = useState(null);
  const [loading,setLoading] = useState(false);

  const isAdminOrSMM = ['admin','smm'].includes(role);

  // ── Load report ──────────────────────────────────────────
  const loadReport = useCallback(async() => {
    setLoading(true);
    try {
      if(isAdminOrSMM) {
        // Use master report endpoint (Phase 2 backend)
        const params = {
          sort_by: sortCol,
          sort_dir: sortDir,
        };
        if(dateFrom) params.date_from = dateFrom;
        if(dateTo)   params.date_to   = dateTo;
        if(filterEditor) params.editor_id = filterEditor;
        if(filterClient) params.client_id  = filterClient;
        if(filterStatus) params.status     = filterStatus;
        if(filterColor)  params.color_bar  = filterColor;
        const r = await api.get('/tasks/reports/master', {params});
        setTasks(r.data.tasks||[]);
        setSummary(r.data.summary||{});
      } else {
        // Editor — own monthly report
        const m = dateFrom ? parseInt(dateFrom.split('-')[1]) : new Date().getMonth()+1;
        const y = dateFrom ? parseInt(dateFrom.split('-')[0]) : new Date().getFullYear();
        const r = await api.get(`/tasks/reports/editor/${userId}`, {params:{month:m,year:y}});
        setTasks([...(r.data.completed_tasks||[]),...(r.data.pending_tasks||[]),...(r.data.overdue_tasks||[])]);
        setSummary({
          total: r.data.total,
          completed: r.data.completed,
          pending: r.data.pending,
          overdue: r.data.overdue,
          rework_total: r.data.rework_count,
        });
      }
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  },[dateFrom,dateTo,filterEditor,filterClient,filterStatus,filterColor,sortCol,sortDir,role,userId]);

  useEffect(()=>{ loadReport(); },[loadReport]);

  // ── Preset buttons ───────────────────────────────────────
  const applyPreset = (p) => {
    setPreset(p);
    if(p!=='custom'){
      const r=getPresetRange(p);
      setDateFrom(r.from);
      setDateTo(r.to);
    }
  };

  // ── Sort handler ─────────────────────────────────────────
  const handleSort = (col) => {
    if(sortCol===col) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({col}) => {
    if(sortCol!==col) return <ArrowUpDown size={10} color={T.textXs}/>;
    return <span style={{fontSize:9,color:T.purple}}>{sortDir==='asc'?'↑':'↓'}</span>;
  };

  // ── Helpers ──────────────────────────────────────────────
  const fmtDuration = (mins) => {
    if(mins===null||mins===undefined) return '—';
    if(mins<60) return `${mins}m`;
    return `${Math.floor(mins/60)}h ${mins%60}m`;
  };

  const fmtDT = (iso) => {
    if(!iso) return '—';
    const d=new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const reworkRate = summary?.total>0 ? Math.round(((summary.rework_total||0)/summary.total)*100) : 0;
  const avgDays = summary?.avg_editing_mins ? Math.round(summary.avg_editing_mins/60/24*10)/10 : null;

  const PRESETS = [
    {v:'today',l:'Today'},
    {v:'week',l:'This Week'},
    {v:'month',l:'This Month'},
    {v:'last_month',l:'Last Month'},
    {v:'custom',l:'Custom'},
  ];

  const TH = ({col, label, width=100}) => (
    <div onClick={()=>handleSort(col)}
      style={{padding:'8px 10px',fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.65)',textTransform:'uppercase',letterSpacing:0.7,display:'flex',alignItems:'center',gap:4,cursor:'pointer',width,flexShrink:0,userSelect:'none'}}>
      {label}<SortIcon col={col}/>
    </div>
  );

  return(
    <div style={{flex:1,overflow:'auto',display:'flex',flexDirection:'column',minHeight:0}}>

      {/* ── SUMMARY CARDS ─────────────────────────────── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:12,marginBottom:16}}>
        {[
          {l:'Total Tasks',    v:summary?.total||0,       c:T.purple,  bg:T.purpleBg,  icon:FileText},
          {l:'Completed',      v:summary?.completed||0,   c:T.green,   bg:T.greenBg,   icon:Check},
          {l:'Pending',        v:summary?.pending||0,     c:T.yellow,  bg:T.yellowBg,  icon:Clock},
          {l:'Overdue',        v:summary?.overdue||0,     c:T.red,     bg:T.redBg,     icon:AlertCircle},
          {l:'Rework Rate',    v:`${reworkRate}%`,        c:T.orange,  bg:T.orangeBg,  icon:TrendingUp},
          ...(avgDays!==null?[{l:'Avg Edit Days',v:`${avgDays}d`,c:T.teal,bg:T.tealBg,icon:Clock}]:[]),
          ...(isAdminOrSMM&&summary?.ready_to_upload?[{l:'Ready to Upload',v:summary.ready_to_upload,c:'#4f46e5',bg:'#e0e7ff',icon:TrendingUp}]:[]),
        ].map(({l,v,c,bg,icon:Icon})=>(
          <div key={l} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'14px 16px',boxShadow:T.shadow}}>
            <div style={{width:34,height:34,borderRadius:9,background:bg,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:8}}>
              <Icon size={15} color={c}/>
            </div>
            <div style={{fontWeight:800,fontSize:22,color:c,lineHeight:1}}>{v}</div>
            <div style={{fontWeight:500,fontSize:11,color:T.textSm,marginTop:4}}>{l}</div>
          </div>
        ))}
      </div>

      {/* ── FILTERS ───────────────────────────────────── */}
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'12px 14px',marginBottom:14,boxShadow:T.shadow}}>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>

          {/* Date presets */}
          <div style={{display:'flex',gap:2,background:T.borderL,borderRadius:9,padding:3}}>
            {PRESETS.map(p=>(
              <button key={p.v} onClick={()=>applyPreset(p.v)}
                style={{background:preset===p.v?T.purple:'transparent',color:preset===p.v?'#fff':T.textSm,border:'none',borderRadius:7,padding:'5px 11px',fontSize:11,fontWeight:preset===p.v?600:400,cursor:'pointer',fontFamily:T.font,transition:'all 0.15s'}}>
                {p.l}
              </button>
            ))}
          </div>

          {/* Custom date range */}
          {preset==='custom'&&(
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
                style={{...inputSt,width:140,fontSize:11,padding:'5px 8px'}}/>
              <span style={{fontSize:11,color:T.textSm}}>to</span>
              <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
                style={{...inputSt,width:140,fontSize:11,padding:'5px 8px'}}/>
            </div>
          )}

          {/* Editor filter — admin/SMM only */}
          {isAdminOrSMM&&(
            <select value={filterEditor} onChange={e=>setFilterEditor(e.target.value)}
              style={{...inputSt,width:150,fontSize:11,padding:'5px 8px'}}>
              <option value="">All Editors</option>
              {editors.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          )}

          {/* Client filter */}
          {isAdminOrSMM&&(
            <select value={filterClient} onChange={e=>setFilterClient(e.target.value)}
              style={{...inputSt,width:160,fontSize:11,padding:'5px 8px'}}>
              <option value="">All Clients</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          )}

          {/* Status filter */}
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
            style={{...inputSt,width:160,fontSize:11,padding:'5px 8px'}}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>

          {/* Priority filter */}
          {isAdminOrSMM&&(
            <div style={{display:'flex',gap:5,alignItems:'center'}}>
              <span style={{fontSize:11,color:T.textXs}}>Priority:</span>
              <button onClick={()=>setFilterColor('')} style={{width:20,height:20,borderRadius:5,background:'#e8e4f3',border:!filterColor?`2px solid ${T.text}`:'2px solid transparent',cursor:'pointer',fontSize:9}} title="All">✕</button>
              {PRIORITY_COLORS.map(p=>(
                <div key={p.color} onClick={()=>setFilterColor(filterColor===p.color?'':p.color)}
                  style={{width:20,height:20,borderRadius:5,background:p.color,cursor:'pointer',border:filterColor===p.color?`2px solid ${T.text}`:'2px solid transparent'}} title={p.label}/>
              ))}
            </div>
          )}

          <Btn variant="secondary" small onClick={()=>{setFilterEditor('');setFilterClient('');setFilterStatus('');setFilterColor('');applyPreset('month');}}>
            <RefreshCw size={11}/>Reset
          </Btn>
          <Btn small onClick={loadReport}><RefreshCw size={11}/>Refresh</Btn>
        </div>
      </div>

      {/* ── NOTE FOR EDITOR ───────────────────────────── */}
      {!isAdminOrSMM&&(
        <div style={{background:T.yellowBg,border:'1px solid #fcd34d',borderRadius:10,padding:'9px 14px',marginBottom:14}}>
          <p style={{fontSize:11,color:'#92400e',fontWeight:500,margin:0}}>📌 Rework count is tracked separately and is NOT counted in your completed total. Only Uploaded tasks count as completed work.</p>
        </div>
      )}

      {/* ── TABLE ─────────────────────────────────────── */}
      {loading ? (
        <div style={{display:'flex',justifyContent:'center',padding:48}}><Spinner/></div>
      ) : tasks.length===0 ? (
        <div style={{textAlign:'center',padding:48,background:T.surface,borderRadius:14,border:`1px solid ${T.border}`}}>
          <BarChart2 size={32} color={T.textXs} style={{margin:'0 auto 12px'}}/>
          <div style={{fontWeight:600,fontSize:15,color:T.text}}>No data for this period</div>
          <div style={{fontSize:12,color:T.textSm,marginTop:4}}>Try adjusting the date range or filters</div>
        </div>
      ) : (
        <div style={{flex:1,overflow:'auto',border:`1px solid ${T.border}`,borderRadius:14,boxShadow:T.shadow}}>

          {/* Table header */}
          <div style={{display:'flex',background:'#1a1035',position:'sticky',top:0,zIndex:10,minWidth:isAdminOrSMM?1600:900}}>
            <TH col="task_code"         label="Code"        width={75}/>
            {isAdminOrSMM&&<TH col="client_name"   label="Client"      width={120}/>}
            <div style={{padding:'8px 10px',fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.65)',textTransform:'uppercase',letterSpacing:0.7,flex:1,minWidth:180}}>Description</div>
            {isAdminOrSMM&&<TH col="assigned_editor" label="Editor"   width={100}/>}
            <TH col="status"            label="Status"      width={120}/>
            {isAdminOrSMM&&<div style={{padding:'8px 10px',fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.65)',textTransform:'uppercase',letterSpacing:0.7,width:60,flexShrink:0}}>Priority</div>}
            <TH col="due_date"          label="Due Date"    width={95}/>
            {isAdminOrSMM&&<TH col="editing_started_at" label="Edit Started" width={130}/>}
            {isAdminOrSMM&&<div style={{padding:'8px 10px',fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.65)',textTransform:'uppercase',letterSpacing:0.7,width:90,flexShrink:0}}>Edit Time</div>}
            <TH col="rework_count"      label="Rework"      width={70}/>
            <TH col="created_at"        label="Created"     width={95}/>
            {isAdminOrSMM&&<TH col="ready_to_upload_at" label="Ready Upload" width={120}/>}
            <div style={{padding:'8px 10px',fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.65)',textTransform:'uppercase',letterSpacing:0.7,width:120,flexShrink:0}}>Uploaded Date</div>
          </div>

          {/* Table rows */}
          {tasks.map((task,idx)=>{
            const cfg = STATUS[task.status]||STATUS.ready_to_edit;
            const priority = PRIORITY_COLORS.find(p=>p.color===task.color_bar);
            const isUploaded = task.status==='uploaded';
            const isOverdueT = task.due_date && task.due_date<new Date().toISOString().split('T')[0] && !isUploaded;

            // Find uploaded date from status_history
            const uploadedEntry = (task.status_history||[]).find(h=>h.to_status==='uploaded');
            const uploadedDate = uploadedEntry?.changed_at||null;

            return(
              <div key={task.id} style={{display:'flex',alignItems:'center',borderBottom:`1px solid ${T.borderL}`,background:idx%2===0?T.surface:'#faf9ff',minWidth:isAdminOrSMM?1600:900,transition:'background 0.1s'}}>

                {/* Code */}
                <div style={{width:75,flexShrink:0,padding:'10px',fontSize:11,fontWeight:700,color:T.purple}}>{task.task_code}</div>

                {/* Client */}
                {isAdminOrSMM&&<div style={{width:120,flexShrink:0,padding:'10px',fontSize:11,color:T.textMd,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{task.client_name||task.client?.company_name||'—'}</div>}

                {/* Description */}
                <div style={{flex:1,minWidth:180,padding:'10px',fontSize:12,color:T.text,fontWeight:500,lineHeight:1.4}}>
                  <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{task.content_description}</div>
                  {task.content_type&&<span style={{fontSize:9,background:task.content_type.color+'22',color:task.content_type.color,padding:'1px 6px',borderRadius:8,fontWeight:600}}>{task.content_type.name}</span>}
                </div>

                {/* Editor */}
                {isAdminOrSMM&&<div style={{width:100,flexShrink:0,padding:'10px',fontSize:11,color:T.textMd}}>{task.editor?.name||'—'}</div>}

                {/* Status */}
                <div style={{width:120,flexShrink:0,padding:'10px'}}>
                  <span style={{background:cfg.bg,color:cfg.color,fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:20,border:`1px solid ${cfg.border}`}}>{cfg.label}</span>
                </div>

                {/* Priority */}
                {isAdminOrSMM&&(
                  <div style={{width:60,flexShrink:0,padding:'10px',display:'flex',alignItems:'center',gap:5}}>
                    <div style={{width:14,height:14,borderRadius:3,background:task.color_bar||'#1868f0',flexShrink:0}}/>
                    <span style={{fontSize:9,color:T.textXs}}>{priority?.label||''}</span>
                  </div>
                )}

                {/* Due Date */}
                <div style={{width:95,flexShrink:0,padding:'10px',fontSize:11,color:isOverdueT?T.red:T.textMd,fontWeight:isOverdueT?700:400}}>
                  {isOverdueT&&'⚠ '}{fmtDate(task.due_date)}
                </div>

                {/* Editing Started */}
                {isAdminOrSMM&&<div style={{width:130,flexShrink:0,padding:'10px',fontSize:10,color:T.textMd}}>{fmtDT(task.editing_started_at)}</div>}

                {/* Edit Duration */}
                {isAdminOrSMM&&(
                  <div style={{width:90,flexShrink:0,padding:'10px',fontSize:11,fontWeight:500,color:task.editing_duration_mins?T.teal:T.textXs}}>
                    {fmtDuration(task.editing_duration_mins)}
                  </div>
                )}

                {/* Rework */}
                <div style={{width:70,flexShrink:0,padding:'10px',textAlign:'center'}}>
                  {task.rework_count>0?(
                    <span style={{background:T.redBg,color:T.red,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20}}>×{task.rework_count}</span>
                  ):<span style={{fontSize:11,color:T.textXs}}>—</span>}
                </div>

                {/* Created */}
                <div style={{width:95,flexShrink:0,padding:'10px',fontSize:10,color:T.textSm}}>{fmtDate(task.created_at?.split('T')[0])}</div>

                {/* Ready to Upload */}
                {isAdminOrSMM&&<div style={{width:120,flexShrink:0,padding:'10px',fontSize:10,color:T.textMd}}>{fmtDT(task.ready_to_upload_at)}</div>}

                {/* Uploaded Date */}
                <div style={{width:120,flexShrink:0,padding:'10px',fontSize:10,color:isUploaded?T.green:T.textXs,fontWeight:isUploaded?600:400}}>
                  {isUploaded?(uploadedDate?fmtDT(uploadedDate):'✓ Uploaded'):'—'}
                </div>
              </div>
            );
          })}

          {/* Row count */}
          <div style={{padding:'10px 14px',borderTop:`1px solid ${T.borderL}`,background:'#faf9ff',fontSize:11,color:T.textSm,display:'flex',justifyContent:'space-between'}}>
            <span>{tasks.length} task{tasks.length!==1?'s':''} shown</span>
            {isAdminOrSMM&&summary&&<span>Rework rate: <strong style={{color:reworkRate>30?T.red:T.textMd}}>{reworkRate}%</strong> · Avg edit time: <strong style={{color:T.teal}}>{avgDays!==null?`${avgDays}d`:'—'}</strong></span>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── MAIN PROJECT PAGE ───────────────────────────────────── */
export default function ProjectPage() {
  const { user } = useAuth();
  const role = user?.role || 'editor';

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null); // null = All Clients (for SMM/admin)
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [editors, setEditors] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);

  // Filters & Sort
  const [mainTab, setMainTab] = useState('active'); // 'active' | 'completed'
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterColor, setFilterColor] = useState(null);
  const [filterDue, setFilterDue] = useState('all'); // 'all' | 'overdue' | 'today' | 'week'
  const [sortBy, setSortBy] = useState('created'); // 'created' | 'due_asc' | 'due_desc' | 'client' | 'status'
  const [searchQuery, setSearchQuery] = useState('');
  const [showSortDrop, setShowSortDrop] = useState(false);
  const sortRef = useRef();

  const [pageView, setPageView] = useState('tasks'); // 'tasks' | 'reports'
  const [showAddTask, setShowAddTask] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const sharedFolderRef = useRef({folderId: null, folderPath: []});

  useEffect(()=>{
    const h=e=>{if(sortRef.current&&!sortRef.current.contains(e.target))setShowSortDrop(false);};
    document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h);
  },[]);

  // Load clients
  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      let list=[];
      if(role==='admin'){
        const r=await api.get('/clients',{params:{status:'active'}});
        list=r.data||[];
      } else if(role==='smm'){
        const r=await api.get('/clients',{params:{status:'active'}});
        list=(r.data||[]);
      } else {
        // Editor — build from tasks (no /clients call)
        const r=await api.get('/tasks');
        const m={};
        (r.data||[]).forEach(t=>{if(t.client_id&&!m[t.client_id])m[t.client_id]={id:t.client_id,company_name:t.client_name||'Unknown',client_code:t.client?.client_code||'',package_name:'',smm:t.smm,editor:t.editor};});
        list=Object.values(m);
      }
      setClients(list);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  },[role,user?.id]);

  useEffect(()=>{
    if(role==='admin'||role==='smm') api.get('/auth/users').then(r=>setEditors((r.data||[]).filter(u=>u.role==='editor'))).catch(()=>{});
    api.get('/tasks/content-types').then(r=>setContentTypes(r.data||[])).catch(()=>{});
    loadClients();
  },[]);

  // Load tasks
  const loadTasks = useCallback(async () => {
    // Editor always loads (their tasks); others need client OR All Clients mode
    if(role!=='editor'&&selectedClient===undefined) return; // undefined = initial unset
    setTaskLoading(true);
    try {
      const params={};
      // null selectedClient = All Clients mode (SMM/admin)
      if(selectedClient&&selectedClient.id) params.client_id=selectedClient.id;
      // Load active tasks
      const r=await api.get('/tasks',{params});
      setTasks(r.data||[]);
      // Load completed separately
      const rc=await api.get('/tasks',{params:{...params,include_completed:true,status:'uploaded'}});
      setCompletedTasks((rc.data||[]).filter(t=>t.status==='uploaded'));
    } catch { toast.error('Failed to load tasks'); }
    finally { setTaskLoading(false); }
  },[selectedClient,role]);

  // selectedClient starts as undefined (unset). Set to null for All Clients once clients load.
  useEffect(()=>{
    if(clients.length>0&&selectedClient===undefined){
      // Restore saved, or default to All Clients (null)
      const saved=localStorage.getItem('nova_pm_client');
      if(saved&&saved!=='null'){
        const found=clients.find(c=>c.id===saved);
        setSelectedClient(found||null);
      } else {
        setSelectedClient(null); // All Clients
      }
    }
  },[clients]);

  useEffect(()=>{ if(selectedClient!==undefined) loadTasks(); },[loadTasks]);

  useEffect(()=>{
    if(selectedClient!==undefined) localStorage.setItem('nova_pm_client',selectedClient?.id||'null');
  },[selectedClient]);

  // Sort + filter tasks
  const applyFilters = (list) => {
    const today=new Date().toISOString().split('T')[0];
    return list.filter(t=>{
      if(searchQuery){ const q=searchQuery.toLowerCase(); if(!t.content_description?.toLowerCase().includes(q)&&!t.task_code?.toLowerCase().includes(q)) return false; }
      if(filterStatus!=='all'&&t.status!==filterStatus) return false;
      if(filterColor&&t.color_bar!==filterColor) return false;
      if(filterDue==='overdue'&&!isOverdue(t.due_date,t.status)) return false;
      if(filterDue==='today'&&!isToday(t.due_date)) return false;
      if(filterDue==='week'&&!isThisWeek(t.due_date)) return false;
      return true;
    });
  };

  const applySort = (list) => {
    return [...list].sort((a,b)=>{
      if(sortBy==='due_asc') return (a.due_date||'9999')>(b.due_date||'9999')?1:-1;
      if(sortBy==='due_desc') return (a.due_date||'0000')<(b.due_date||'0000')?1:-1;
      if(sortBy==='client') return (a.client_name||'').localeCompare(b.client_name||'');
      if(sortBy==='status') return (a.status||'').localeCompare(b.status||'');
      return new Date(b.created_at)-new Date(a.created_at); // recent
    });
  };

  const filteredTasks = applySort(applyFilters(mainTab==='active'?tasks:completedTasks));
  const statusCounts = tasks.reduce((acc,t)=>({...acc,[t.status]:(acc[t.status]||0)+1}),{});
  const canCreateTask = ['admin','smm'].includes(role);
  const isAllClients = selectedClient===null;
  const showClientBadge = isAllClients || role==='editor';

  const SORT_OPTS = [
    {v:'created',l:'Recent First'},
    {v:'due_asc',l:'Due Date (Earliest)'},
    {v:'due_desc',l:'Due Date (Latest)'},
    {v:'client',l:'Client Name A-Z'},
    {v:'status',l:'Status'},
  ];

  return(
  <div style={{fontFamily:T.font,display:'flex',flexDirection:'column',height:'100vh',overflow:'hidden',padding:'0 0 0 0'}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── TOP BAR ──────────────────────────────────────── */}
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap',paddingTop:20,paddingLeft:20,paddingRight:20}}>
        <div>
          <h1 style={{fontWeight:700,fontSize:22,color:T.text,marginBottom:2}}>Project Management</h1>
          <p style={{fontSize:12,color:T.textSm}}>Content tracker · {role==='editor'?'Your assigned tasks':'Manage client content'}</p>
        </div>

        {/* ── PAGE VIEW TABS ───────────────────────── */}
        <div style={{display:'flex',gap:2,background:T.borderL,borderRadius:10,padding:3}}>
          <button onClick={()=>setPageView('tasks')}
            style={{display:'flex',alignItems:'center',gap:6,background:pageView==='tasks'?T.surface:'transparent',border:'none',color:pageView==='tasks'?T.purple:T.textSm,borderRadius:8,padding:'6px 13px',fontSize:12,fontWeight:pageView==='tasks'?600:400,cursor:'pointer',fontFamily:T.font,boxShadow:pageView==='tasks'?T.shadow:'none',transition:'all 0.15s'}}>
            <FileText size={13}/>Tasks
          </button>
          <button onClick={()=>setPageView('reports')}
            style={{display:'flex',alignItems:'center',gap:6,background:pageView==='reports'?T.surface:'transparent',border:'none',color:pageView==='reports'?T.purple:T.textSm,borderRadius:8,padding:'6px 13px',fontSize:12,fontWeight:pageView==='reports'?600:400,cursor:'pointer',fontFamily:T.font,boxShadow:pageView==='reports'?T.shadow:'none',transition:'all 0.15s'}}>
            <BarChart2 size={13}/>Reports
          </button>
        </div>

        {/* ── CLIENT CHIPS (SMM / Admin) ─────────────── */}
{/* ── CLIENT DROPDOWN - All roles*/}
{(
  <div style={{display:'flex',alignItems:'center',gap:8,marginLeft:'auto'}}>
    <label style={{fontSize:12,fontWeight:600,color:'#8b7fb0',whiteSpace:'nowrap'}}>Client:</label>
    <select
      value={selectedClient?.id||''}
      onChange={e=>{
        if(!e.target.value){ setSelectedClient(null); return; }
        const found=clients.find(c=>c.id===e.target.value);
        if(found) setSelectedClient(found);
      }}
      style={{border:'1.5px solid #e8e4f3',borderRadius:9,padding:'7px 12px',fontSize:13,fontFamily:"'Poppins',sans-serif",color:'#1a1035',background:'#fff',outline:'none',cursor:'pointer',minWidth:180,maxWidth:260,fontWeight:600}}
    >
      <option value="">All Clients ({clients.length})</option>
      {clients.map(c=>(
        <option key={c.id} value={c.id}>{c.company_name}</option>
      ))}
    </select>
    {selectedClient&&(
      <span style={{fontSize:11,color:'#8b7fb0',background:'#f5f3ff',padding:'3px 9px',borderRadius:10,whiteSpace:'nowrap'}}>
        {selectedClient.client_code}
      </span>
    )}
  </div>
)}

        {canCreateTask&&(selectedClient||isAllClients)&&selectedClient!==undefined&&(
          <Btn onClick={()=>setShowAddTask(true)}><Plus size={14}/>Add Task</Btn>
        )}
        <Btn variant="ghost" onClick={loadTasks} small><RefreshCw size={13}/></Btn>
      </div>

      {/* ── TASKS VIEW ───────────────────────────── */}
      {pageView==='tasks'&&<>

      {/* ── CLIENT INFO STRIP (single client selected) ── */}
      {selectedClient&&selectedClient.id&&(
        <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:'10px 16px',marginBottom:12,display:'flex',gap:18,flexWrap:'wrap',alignItems:'center',boxShadow:T.shadow}}>
          <div>
            <div style={{fontWeight:700,fontSize:14,color:T.text}}>{selectedClient.company_name}</div>
            <div style={{fontSize:11,color:T.textSm}}>{selectedClient.client_code}</div>
          </div>
          {selectedClient.smm?.name&&<div><div style={{fontSize:10,color:T.textXs}}>SMM</div><div style={{fontSize:12,fontWeight:600,color:T.text}}>{selectedClient.smm.name}</div></div>}
          {selectedClient.editor?.name&&<div><div style={{fontSize:10,color:T.textXs}}>Editor</div><div style={{fontSize:12,fontWeight:600,color:T.text}}>{selectedClient.editor.name}</div></div>}
          <div><div style={{fontSize:10,color:T.textXs}}>Active</div><div style={{fontSize:14,fontWeight:700,color:T.purple}}>{tasks.length}</div></div>
          <div><div style={{fontSize:10,color:T.textXs}}>Uploaded</div><div style={{fontSize:14,fontWeight:700,color:T.green}}>{completedTasks.length}</div></div>
          <div><div style={{fontSize:10,color:T.textXs}}>Rework</div><div style={{fontSize:14,fontWeight:700,color:T.red}}>{tasks.filter(t=>['rework','client_rework','correction'].includes(t.status)).length}</div></div>
        </div>
      )}

      {/* ── ACTIVE / COMPLETED TABS + FILTERS ─────────── */}
      {(selectedClient!==undefined)&&(
        <div style={{marginBottom:10}}>
          {/* Main tabs */}
          <div style={{display:'flex',gap:0,borderBottom:`2px solid ${T.borderL}`,marginBottom:10}}>
            {[{id:'active',l:`Active (${tasks.length})`},{id:'completed',l:`Uploaded (${completedTasks.length})`}].map(t=>(
              <button key={t.id} onClick={()=>{setMainTab(t.id);setFilterStatus('all');}}
                style={{background:'none',border:'none',padding:'8px 16px',fontSize:13,fontWeight:mainTab===t.id?600:400,color:mainTab===t.id?T.purple:T.textSm,cursor:'pointer',fontFamily:T.font,borderBottom:mainTab===t.id?`2px solid ${T.purple}`:'2px solid transparent',marginBottom:-2}}>
                {t.l}
              </button>
            ))}
          </div>

          {/* Filter row */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            {/* Search */}
            <div style={{display:'flex',alignItems:'center',gap:7,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'6px 12px',minWidth:190}}>
              <Search size={13} color={T.textXs}/>
              <input style={{background:'transparent',border:'none',outline:'none',fontSize:12,fontFamily:T.font,color:T.text,flex:1}} placeholder="Search tasks..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}/>
              {searchQuery&&<X size={12} style={{cursor:'pointer'}} onClick={()=>setSearchQuery('')} color={T.textXs}/>}
            </div>

            {/* Sort dropdown */}
            <div ref={sortRef} style={{position:'relative'}}>
              <button onClick={()=>setShowSortDrop(o=>!o)} style={{display:'flex',alignItems:'center',gap:6,background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'6px 12px',fontSize:12,fontFamily:T.font,color:T.textMd,cursor:'pointer',fontWeight:500}}>
                <ArrowUpDown size={13} color={T.purple}/>{SORT_OPTS.find(s=>s.v===sortBy)?.l||'Sort'}
              </button>
              {showSortDrop&&(
                <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:100,background:'#fff',border:`1px solid ${T.border}`,borderRadius:10,boxShadow:T.shadowMd,minWidth:185,overflow:'hidden'}}>
                  {SORT_OPTS.map(s=>(
                    <button key={s.v} onClick={()=>{setSortBy(s.v);setShowSortDrop(false);}}
                      style={{display:'flex',alignItems:'center',gap:8,width:'100%',padding:'9px 14px',background:sortBy===s.v?T.purpleBg:'transparent',border:'none',cursor:'pointer',fontFamily:T.font,fontSize:12,fontWeight:sortBy===s.v?600:400,color:T.text,textAlign:'left'}}>
                      {s.l}{sortBy===s.v&&<Check size={11} color={T.purple} style={{marginLeft:'auto'}}/>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Due date filter */}
            <div style={{display:'flex',background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,overflow:'hidden'}}>
              {[['all','All'],['overdue','Overdue'],['today','Today'],['week','This Week']].map(([v,l])=>(
                <button key={v} onClick={()=>setFilterDue(filterDue===v?'all':v)}
                  style={{background:filterDue===v?T.redBg:'transparent',border:'none',color:filterDue===v?T.red:T.textSm,cursor:'pointer',padding:'6px 11px',fontSize:11,fontWeight:filterDue===v?600:400,fontFamily:T.font,whiteSpace:'nowrap'}}>
                  {l}
                </button>
              ))}
            </div>

            {/* Color bar filter */}
            <div style={{display:'flex',gap:6,alignItems:'center',background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,padding:'6px 10px'}}>
              <span style={{fontSize:11,color:T.textXs}}>Priority:</span>
              <button onClick={()=>setFilterColor(null)} style={{width:18,height:18,borderRadius:4,background:'#e8e4f3',border:filterColor===null?`2px solid ${T.text}`:'2px solid transparent',cursor:'pointer',fontSize:9,display:'flex',alignItems:'center',justifyContent:'center',color:T.textSm}} title="All">✕</button>
              {PRIORITY_COLORS.map(p=>(
                <div key={p.color} onClick={()=>setFilterColor(filterColor===p.color?null:p.color)}
                  style={{width:18,height:18,borderRadius:4,background:p.color,cursor:'pointer',border:filterColor===p.color?`2px solid ${T.text}`:'2px solid transparent',transition:'all 0.1s'}} title={p.label}/>
              ))}
            </div>

{/* ── Quick count badges always visible for admin/smm ── */}
{mainTab==='active' && ['admin','smm'].includes(role) && (
  <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
    {[
      {key:'editing',         label:'Editing',         color:'#0d9488', bg:'#ccfbf1', border:'#5eead4'},
      {key:'review',          label:'Review',          color:'#7c3aed', bg:'#ede9fe', border:'#c4b5fd'},
      {key:'rework',          label:'Rework',          color:'#dc2626', bg:'#fee2e2', border:'#fca5a5'},
      {key:'client_approval', label:'Client Approval', color:'#2563eb', bg:'#dbeafe', border:'#93c5fd'},
      {key:'client_rework',   label:'Client Rework',   color:'#ea580c', bg:'#ffedd5', border:'#fdba74'},
    ].map(({key,label,color,bg,border})=>{
      const count = statusCounts[key]||0;
      const isActive = filterStatus===key;
      return(
        <button key={key} onClick={()=>setFilterStatus(isActive?'all':key)}
          style={{display:'flex',alignItems:'center',gap:5,
            background:isActive?color:bg, border:`1.5px solid ${border}`,
            color:isActive?'#fff':color, borderRadius:20, padding:'5px 12px',
            fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Poppins',sans-serif",
            transition:'all 0.15s', boxShadow:isActive?`0 2px 8px ${color}44`:'none'}}>
          {label}
          <span style={{background:isActive?'rgba(255,255,255,0.3)':color,
            color:'#fff', borderRadius:20, padding:'1px 7px',
            fontSize:10, fontWeight:800, minWidth:20, textAlign:'center'}}>
            {count}
          </span>
        </button>
      );
    })}
  </div>
)}
          </div>
        </div>
      )}

      {/* ── EMPTY STATE ───────────────────────────────── */}
      {selectedClient===undefined&&role!=='editor'&&(
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flex:1,gap:14,paddingTop:60}}>
          <div style={{width:72,height:72,background:T.purpleBg,borderRadius:18,display:'flex',alignItems:'center',justifyContent:'center'}}><Building2 size={32} color={T.purple}/></div>
          <div style={{fontWeight:600,fontSize:16,color:T.text}}>Loading clients...</div>
        </div>
      )}

      {/* ── TABLE ─────────────────────────────────────── */}
      {selectedClient!==undefined&&(
  <div style={{flex:1,overflow:'auto',border:`1px solid ${T.border}`,borderRadius:14,boxShadow:T.shadow,margin:'0 20px 20px 20px'}}>
          {/* Header */}
          <div style={{display:'grid',gridTemplateColumns:'10px 40px 40px 1.6fr 130px 110px 110px 140px 55px 150px 140px 110px 120px',background:'#1a1035',position:'sticky',top:0,zIndex:10,minWidth:1380}}>
            {['','S.NO','','CONTENT DESCRIPTION','STATUS','EDITOR','TYPE','RAW FOOTAGE','CTA','CTA LINK / DOC','EDITED VIDEO','DUE DATE','CLIENT STATUS'].map((h,i)=>(
              <div key={i} style={{padding:'10px 8px',fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.6)',textTransform:'uppercase',letterSpacing:0.8,borderRight:i<12?'1px solid rgba(255,255,255,0.06)':'none',display:'flex',alignItems:'center'}}>{h}</div>
            ))}
          </div>

          {/* Loading */}
          {taskLoading&&<div style={{display:'flex',justifyContent:'center',padding:48}}><Spinner/></div>}

          {/* Empty */}
          {!taskLoading&&filteredTasks.length===0&&(
            <div style={{padding:48,textAlign:'center',background:T.surface}}>
              <div style={{width:52,height:52,background:T.purpleBg,borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}><FileText size={24} color={T.purple}/></div>
              <div style={{fontWeight:600,fontSize:15,color:T.text,marginBottom:6}}>{searchQuery?'No tasks match your search':mainTab==='completed'?'No uploaded tasks yet':'No active tasks'}</div>
              {canCreateTask&&!searchQuery&&mainTab==='active'&&<Btn onClick={()=>setShowAddTask(true)}><Plus size={14}/>Add First Task</Btn>}
            </div>
          )}

          {/* Rows */}
          {!taskLoading&&filteredTasks.map((task,idx)=>(
  <TaskRow key={task.id} task={task} role={role} sno={idx+1} editors={editors} contentTypes={contentTypes}
    onRefresh={loadTasks} onViewDetail={setDetailTask} showClientBadge={showClientBadge}
    sharedFolderRef={sharedFolderRef}/>
))}
        </div>
      )}

      </> /* end pageView==='tasks' */}

      {/* ── MODALS ────────────────────────────────────── */}
      {showAddTask&&(selectedClient||selectedClient===null)&&(
        <AddTaskModal
          clientId={selectedClient?.id||null}
          clientName={selectedClient?.company_name||''}
          editors={editors}
          contentTypes={contentTypes}
          onClose={()=>setShowAddTask(false)}
          onSaved={()=>{setShowAddTask(false);loadTasks();}}
        />
      )}

      {detailTask&&(
        <TaskDetailPanel task={detailTask} role={role}
          onClose={()=>setDetailTask(null)}
          onRefresh={()=>{loadTasks();api.get(`/tasks/${detailTask.id}`).then(r=>setDetailTask(r.data)).catch(()=>{});}}
        />
      )}

      {/* ── REPORTS VIEW ─────────────────────────── */}
      {pageView==='reports'&&(
        <MasterReportsSection role={role} userId={user?.id} editors={editors} clients={clients}/>
      )}
    </div>
  );
}